import React, { useState, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  TextField,
  Collapse,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  AccountBalanceWallet as ARIcon,
  EventBusy as EventBusyIcon,
  RequestQuote as RequestQuoteIcon,
  Event as EventIcon,
  FolderOpen as FolderOpenIcon,
  Vaccines as VaccinesIcon,
  Inventory as StockIcon,
  ReceiptLong as ReceiptLongIcon,
  ExpandMore as ExpandMoreIcon,
  Tune as TuneIcon,
  FileDownload as ExportIcon,
  Payments as PaymentsIcon,
  Warning as WarningIcon,
  Person as PersonIcon,
  CalendarMonth as CalendarMonthIcon,
  Send as SendIcon,
  CheckCircle as CheckCircleIcon,
  ErrorOutline as ErrorOutlineIcon,
  Email as EmailIcon,
  Forum as ForumIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { useRBAC } from '../../auth/useRBAC';
import { REPORTS_ACCESS_UNKNOWN_MESSAGE, reportsAccessUnknown } from '../../auth/canOpenReports';
import { formatDollars, formatCents } from '../../utils/money';
import { formatCalendarDate, formatPaymentDay, INSTANT_FORMATS } from '../../utils/dateUtils';
import { useClinicTimezone, useFormatInstant } from '../../hooks/useClinicTimezone';
import {
  withOfflineFallback,
  readRevenueByMonth,
  readAppointmentTypes,
  readUpcomingVaccinations,
  readInventoryStatus,
  useIsOffline,
} from '../../offline';

// ─── Design tokens ────────────────────────────────────────────────────────────

const T = {
  brand: '#8B4513',
  amber: '#C9772B',
  bandBg: '#F3ECE2',
  bandText: '#6E4A2A',
  cardBorder: '#E8E2D9',
  inputBorder: '#D8D0C4',
  textPrimary: '#2A2420',
  textSecondary: '#6B6259',
  textMuted: '#8A8077',
  pageBg: '#FAF9F6',
  error: '#C0392B',
  errorTint: '#FBEDEA',
  warning: '#C9772B',
  warningTint: '#FBF3E8',
  warningText: '#B5651D',
  success: '#2E7D32',
  successTint: '#E7F4E8',
};

// ─── Types ────────────────────────────────────────────────────────────────────

interface KpiSummary {
  mtdRevenue: number;
  mtdRevenueChange: number | null;
  outstandingAR: number;
  outstandingARCount: number;
  overdueAR: number;
  overdueARCount: number;
  unbilledCharges: number;
  unbilledChargesCount: number;
  appointmentsToday: number;
  openRecords: number;
  vaccinesDueSoon: number;
  lowStockItems: number;
}

interface ArAgingItem {
  id: string;
  invoiceNumber: string;
  horseName: string;
  clientName: string;
  issuedAt: string;
  dueDate: string;
  amount: number;
  status: string;
  daysOverdue: number;
}

interface ArAgingBucket {
  invoices: ArAgingItem[];
  total: number;
  count: number;
}

interface ArAgingReport {
  buckets: {
    current: ArAgingBucket;
    '1-30': ArAgingBucket;
    '31-60': ArAgingBucket;
    '61-90': ArAgingBucket;
    '90+': ArAgingBucket;
  };
  totalAR: number;
  totalCount: number;
}

interface InvoiceStatusRow {
  status: string;
  count: number;
  total: number;
}

interface ChargesUnbilledItem {
  id: string;
  horseName: string;
  procedureName: string;
  category: string | null;
  date: string;
  unitPrice: number;
  performedBy: string | null;
}

interface ChargesUnbilledData {
  items: ChargesUnbilledItem[];
  total: number;
  count: number;
}

interface PaymentItem {
  id: string;
  date: string;
  invoiceNumber: string;
  horseName: string;
  amountCents: number;
  paymentMethod: string;
  paymentType: string | null;
  referenceNote: string | null;
}

interface PaymentsSummaryData {
  items: PaymentItem[];
  totalCents: number;
  count: number;
}

interface RevenueData { month: string; amount: number }
interface AppointmentType { type: string; count: number; color: string }
interface VaccinationSchedule { horseName: string; ownerName: string; vaccineType: string; dueDate: string }
interface InventoryItem { name: string; category: string; status: 'Low Stock' | 'Warning' | 'OK'; currentStock: number; minRequired: number }

interface ReportFilters {
  startDate: string;
  endDate: string;
}

// The appointment reports read a missing range as their own default window (the dashboard's last 30
// or 90 days), so the Appointments tab spells "All time" out as a start date.
const ALL_TIME_START = '1970-01-01';

// ─── CSV export ───────────────────────────────────────────────────────────────

function exportCsv(filename: string, headers: string[], rows: (string | number)[][]) {
  const csv = [headers, ...rows].map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

// ─── What each report needs, mirroring the server ─────────────────────────────

/**
 * Report id → the ROUTE it reads and the slugs `ReportsController` requires for that route. ONE
 * table, deliberately: the server enforces a conjunction PER ROUTE (ADR-0035 A8), and the only way
 * this page can hide exactly what it cannot open — rather than what somebody guessed it could not
 * open — is to mirror that table.
 *
 * Reading it: `view:reports` opens the page at all (`ReportsRoute` in App.tsx already decided
 * that, so it is redundant here — and it is listed anyway, because a copy of the server's
 * conjunction that silently omits a term is the kind of copy that rots). The SECOND slug is what
 * actually splits the audience: a person with `view:reports` but without `view:financial_reports`
 * sees the operational reports and none of the money.
 *
 * ⚠ `route` IS THE WELD, added by the review's fix wave (I3). Without it this table is a hand copy
 * of a server decision with nothing checking the copy, and the failure mode is silent in the
 * dangerous direction: a term dropped here shows a person a section whose request 403s, and a term
 * dropped on the SERVER opens a report this page would still have hidden. `role-gate-binding.spec.ts`
 * parses this file, joins on `route`, and asserts each `requires` EQUALS what the controller
 * declares for that route — so the two tables cannot disagree in either direction.
 *
 * Every id below is either an `expandedReports` key or a root-level query key, so nothing on the
 * page is gated by a string that exists only in this table.
 */
export const REPORT_REQUIREMENTS: Record<string, { route: string; requires: readonly string[] }> = {
  // Financial — the `reports/financial` family, which keeps `view:billing` as a third term because
  // read the invoice and payment rows that slug has always gated.
  'ar-aging': { route: 'financial/ar-aging', requires: ['view:reports', 'view:financial_reports', 'view:billing'] },
  'invoice-status': { route: 'financial/invoice-status', requires: ['view:reports', 'view:financial_reports', 'view:billing'] },
  'charges-unbilled': { route: 'financial/charges-unbilled', requires: ['view:reports', 'view:financial_reports', 'view:billing'] },
  'payments-summary': { route: 'financial/payments-summary', requires: ['view:reports', 'view:financial_reports', 'view:billing'] },
  'ar-by-provider': { route: 'financial/ar-by-provider', requires: ['view:reports', 'view:financial_reports', 'view:billing'] },
  'ar-by-service-date': { route: 'financial/ar-by-service-date', requires: ['view:reports', 'view:financial_reports', 'view:billing'] },
  'ar-by-billed-date': { route: 'financial/ar-by-billed-date', requires: ['view:reports', 'view:financial_reports', 'view:billing'] },
  'ar-communications': { route: 'financial/ar-communications', requires: ['view:reports', 'view:financial_reports', 'view:billing'] },
  'client-balances': { route: 'financial/client-balances', requires: ['view:reports', 'view:financial_reports', 'view:billing'] },
  'production-by-provider': { route: 'financial/production-by-provider', requires: ['view:reports', 'view:financial_reports', 'view:billing'] },
  // Financial — no `view:billing`: these TWO aggregate rather than list invoices. The server has a
  // third route on the same conjunction, `/reports/stats`, and this page has never requested it —
  // the header numbers come from `/reports/kpi-summary`. It has no row here for that reason, and
  // the weld allows the page to be a SUBSET of the controller's routes while forbidding a
  // disagreement about any route it does name. (`/reports/breeds` is the same case.)
  revenue: { route: 'revenue', requires: ['view:reports', 'view:financial_reports'] },
  'kpi-summary': { route: 'kpi-summary', requires: ['view:reports', 'view:financial_reports'] },
  // Operational — the page plus the data slug the route already named.
  'appointment-types': { route: 'appointments', requires: ['view:reports', 'view:appointments'] },
  'appointment-outcomes': { route: 'appointments/outcomes', requires: ['view:reports', 'view:appointments'] },
  'upcoming-vaccinations': { route: 'vaccinations', requires: ['view:reports', 'view:medical_records'] },
  'inventory-status': { route: 'inventory', requires: ['view:reports', 'view:inventory'] },
};

/**
 * May this person open this report? ALL-of, exactly as `RoleGuard` reads a conjunction.
 *
 * An UNKNOWN key returns false rather than true: a report added without a row here is hidden until
 * someone writes the row, which fails toward the safe side on a page full of financial data.
 */
export const canSeeReport = (key: string, permissions: readonly string[]): boolean => {
  const entry = REPORT_REQUIREMENTS[key];
  if (!entry) return false;
  return entry.requires.every(slug => permissions.includes(slug));
};

/** The financial ids, so "can this person see any money at all" is one call rather than ten. */
const FINANCIAL_REPORT_IDS = [
  'ar-aging', 'invoice-status', 'charges-unbilled', 'payments-summary', 'ar-by-provider',
  'ar-by-service-date', 'ar-by-billed-date', 'ar-communications', 'client-balances',
  'production-by-provider',
] as const;

// ─── KPI Hero Card (MTD Revenue) ──────────────────────────────────────────────

interface KpiHeroProps {
  value: string;
  trend: number | null;
  loading: boolean;
}

const KpiHeroCard: React.FC<KpiHeroProps> = ({ value, trend, loading }) => (
  <Box
    sx={{
      flex: '0 0 auto',
      width: { xs: '100%', md: 290 },
      bgcolor: T.brand,
      borderRadius: '14px',
      p: '22px 24px',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      color: '#fff',
      minHeight: 130,
    }}
  >
    <Box display="flex" justifyContent="space-between" alignItems="flex-start">
      {/* Explicit #fff: the theme's Typography default otherwise overrides the
          container's inherited color and renders dark-on-brown. */}
      <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, letterSpacing: '0.6px', textTransform: 'uppercase', opacity: 0.82, color: '#fff' }}>
        Month-to-Date Revenue
      </Typography>
      <PaymentsIcon sx={{ fontSize: 18, opacity: 0.85, color: '#fff' }} />
    </Box>
    <Typography sx={{ fontSize: { xs: '2rem', sm: '2.4rem' }, fontWeight: 700, lineHeight: 1.05, letterSpacing: '-0.5px', my: 1, color: '#fff' }}>
      {loading ? '—' : value}
    </Typography>
    {trend != null && (
      <Box
        display="inline-flex"
        alignItems="center"
        gap={0.5}
        sx={{ bgcolor: 'rgba(255,255,255,.18)', px: '11px', py: '4px', borderRadius: '20px', width: 'fit-content' }}
      >
        {trend >= 0 ? <TrendingUpIcon sx={{ fontSize: 16, color: '#fff' }} /> : <TrendingDownIcon sx={{ fontSize: 16, color: '#fff' }} />}
        <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#fff' }}>
          {trend >= 0 ? '+' : ''}{trend.toFixed(1)}% vs last month
        </Typography>
      </Box>
    )}
  </Box>
);

// ─── KPI Metric Tile ──────────────────────────────────────────────────────────

interface KpiTileProps {
  title: string;
  value: string;
  subtitle: string;
  icon: React.ReactNode;
  alertLevel?: 'error' | 'warning';
  onClick?: () => void;
}

const KpiTile: React.FC<KpiTileProps> = ({ title, value, subtitle, icon, alertLevel, onClick }) => {
  const accentColor = alertLevel === 'error' ? T.error : alertLevel === 'warning' ? T.warning : undefined;
  const tintBg = alertLevel === 'error' ? T.errorTint : alertLevel === 'warning' ? T.warningTint : '#fff';

  return (
    <Box
      onClick={onClick}
      sx={{
        position: 'relative',
        bgcolor: tintBg,
        border: `1px solid ${T.cardBorder}`,
        borderLeft: accentColor ? `3px solid ${accentColor}` : `1px solid ${T.cardBorder}`,
        borderRadius: '10px',
        p: '11px 13px',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'box-shadow 0.15s, transform 0.15s',
        '&:hover': onClick ? { boxShadow: '0 5px 14px rgba(0,0,0,.09)', transform: 'translateY(-2px)' } : {},
      }}
    >
      {accentColor && (
        <Box sx={{ position: 'absolute', top: 10, right: 10, width: 8, height: 8, borderRadius: '50%', bgcolor: accentColor }} />
      )}
      <Box display="flex" alignItems="center" gap={0.75} mb={1}>
        <Box sx={{ color: accentColor || T.brand, display: 'flex', alignItems: 'center' }}>
          {icon}
        </Box>
      </Box>
      <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.4px', textTransform: 'uppercase', color: T.textMuted }}>
        {title}
      </Typography>
      <Typography sx={{ fontSize: '1.45rem', fontWeight: 700, color: T.textPrimary, mt: '8px', lineHeight: 1 }}>
        {value}
      </Typography>
      <Typography sx={{ fontSize: '0.68rem', color: T.textSecondary, mt: '3px' }}>
        {subtitle}
      </Typography>
    </Box>
  );
};

// ─── Filter Bar ───────────────────────────────────────────────────────────────
// Date range only. The old Provider/Barn/Client controls were hardcoded demo
// names that were never sent to the backend — a filter that doesn't filter.

const localIso = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const DATE_PRESETS: { label: string; range: () => ReportFilters }[] = [
  { label: 'All time', range: () => ({ startDate: '', endDate: '' }) },
  {
    label: 'This month',
    range: () => ({ startDate: localIso(new Date(new Date().getFullYear(), new Date().getMonth(), 1)), endDate: '' }),
  },
  {
    label: 'Last month',
    range: () => {
      const n = new Date();
      return {
        startDate: localIso(new Date(n.getFullYear(), n.getMonth() - 1, 1)),
        endDate: localIso(new Date(n.getFullYear(), n.getMonth(), 0)),
      };
    },
  },
  {
    label: 'Last 90 days',
    range: () => ({ startDate: localIso(new Date(Date.now() - 90 * 86400000)), endDate: '' }),
  },
  {
    label: 'Year to date',
    range: () => ({ startDate: localIso(new Date(new Date().getFullYear(), 0, 1)), endDate: '' }),
  },
];

interface FilterBarProps {
  filters: ReportFilters;
  onChange: (f: ReportFilters) => void;
  onApply: () => void;
  /** One-click presets set AND apply the range immediately. */
  onPreset: (f: ReportFilters) => void;
}

const controlSx = {
  '& .MuiOutlinedInput-root': {
    fontSize: '0.81rem',
    color: T.textPrimary,
    bgcolor: '#fff',
    borderRadius: '8px',
    '& fieldset': { borderColor: T.inputBorder },
  },
};

const labelSx = { fontSize: '0.69rem', fontWeight: 600, textTransform: 'uppercase' as const, color: T.textSecondary, letterSpacing: '0.4px', mb: '4px' };

const FilterBar: React.FC<FilterBarProps> = ({ filters, onChange, onApply, onPreset }) => {
  const reset = () => onPreset({ startDate: '', endDate: '' });

  return (
    <Paper elevation={0} sx={{ border: `1px solid ${T.cardBorder}`, borderRadius: '12px', p: '14px 16px', mb: 2.5 }}>
      <Box display="flex" gap={0.75} flexWrap="wrap" mb={1.25}>
        {DATE_PRESETS.map(p => {
          const r = p.range();
          const isActive = filters.startDate === r.startDate && filters.endDate === r.endDate;
          return (
            <Chip
              key={p.label}
              label={p.label}
              size="small"
              onClick={() => onPreset(r)}
              sx={{
                fontSize: '0.74rem',
                fontWeight: 600,
                bgcolor: isActive ? T.brand : T.bandBg,
                color: isActive ? '#fff' : T.bandText,
                '&:hover': { bgcolor: isActive ? '#6d3610' : '#EBE2D5' },
              }}
            />
          );
        })}
      </Box>
      <Box display="flex" alignItems="flex-end" gap={1.75} flexWrap="wrap">
        <TuneIcon sx={{ color: T.brand, fontSize: 20, alignSelf: 'center', flexShrink: 0 }} />

        <Box>
          <Typography sx={labelSx}>From</Typography>
          <TextField
            size="small"
            type="date"
            value={filters.startDate}
            onChange={e => onChange({ ...filters, startDate: e.target.value })}
            InputLabelProps={{ shrink: true }}
            sx={{ ...controlSx, width: 145 }}
          />
        </Box>

        <Box>
          <Typography sx={labelSx}>To</Typography>
          <TextField
            size="small"
            type="date"
            value={filters.endDate}
            onChange={e => onChange({ ...filters, endDate: e.target.value })}
            InputLabelProps={{ shrink: true }}
            sx={{ ...controlSx, width: 145 }}
          />
        </Box>

        <Box sx={{ flex: 1 }} />

        <Button size="small" variant="text" onClick={reset} sx={{ color: T.textSecondary, fontSize: '0.8rem', alignSelf: 'flex-end', mb: '2px' }}>
          Reset
        </Button>
        <Button
          size="small"
          variant="contained"
          onClick={onApply}
          sx={{ bgcolor: T.brand, color: '#fff', borderRadius: '9px', px: '18px', py: '9px', fontSize: '0.8rem', fontWeight: 600, alignSelf: 'flex-end', '&:hover': { bgcolor: '#6d3610' } }}
        >
          Apply
        </Button>
      </Box>
    </Paper>
  );
};

// ─── Section Header (band style) ─────────────────────────────────────────────

const SectionHeader: React.FC<{ label: string }> = ({ label }) => (
  <Box
    display="flex"
    alignItems="center"
    gap={1.5}
    sx={{ mt: '22px', mb: '14px', px: '15px', py: '10px', bgcolor: T.bandBg, borderLeft: `4px solid ${T.brand}`, borderRadius: '8px' }}
  >
    <Typography sx={{ fontSize: '0.81rem', fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', color: T.bandText }}>
      {label}
    </Typography>
  </Box>
);

// ─── Expandable Report Row ────────────────────────────────────────────────────

interface ExpandableReportProps {
  id: string;
  title: string;
  subtitle?: string;
  icon: React.ReactNode;
  rightChip?: React.ReactNode;
  isOpen: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}

const ExpandableReport: React.FC<ExpandableReportProps> = ({ title, subtitle, icon, rightChip, isOpen, onToggle, children }) => (
  <Paper
    elevation={0}
    sx={{ border: `1px solid ${isOpen ? T.amber : T.cardBorder}`, borderRadius: '12px', overflow: 'hidden', mb: 1.5 }}
  >
    <Box
      display="flex"
      alignItems="center"
      gap={1.5}
      px={2.25}
      py={2}
      onClick={onToggle}
      sx={{ cursor: 'pointer', bgcolor: isOpen ? 'rgba(139,69,19,.03)' : '#fff', '&:hover': { bgcolor: 'rgba(139,69,19,.03)' } }}
    >
      <Box sx={{ color: T.brand, display: 'flex', alignItems: 'center', flexShrink: 0, fontSize: 22 }}>
        {icon}
      </Box>
      <Box flex={1} minWidth={0}>
        <Typography sx={{ fontSize: '0.94rem', fontWeight: 600, color: T.textPrimary }}>{title}</Typography>
        {subtitle && <Typography sx={{ fontSize: '0.75rem', color: T.textSecondary, mt: '2px' }}>{subtitle}</Typography>}
      </Box>
      {rightChip && <Box flexShrink={0}>{rightChip}</Box>}
      <ExpandMoreIcon
        sx={{
          color: T.brand,
          fontSize: 24,
          flexShrink: 0,
          transition: 'transform .3s',
          transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
        }}
      />
    </Box>
    {/* unmountOnExit: a collapsed panel must not mount (and so not fetch) its
        report — with all panels mounted, opening Reports fired every
        /reports/financial/* query at once. react-query caches across remounts,
        so re-expanding a previously-loaded panel is instant. */}
    <Collapse in={isOpen} unmountOnExit>
      <Box px={2.25} pb={2.5} pt={0.5}>
        {children}
      </Box>
    </Collapse>
  </Paper>
);

// ─── AR Aging Report ──────────────────────────────────────────────────────────

const AGING_BUCKETS = [
  { key: 'current' as const, label: 'Current', color: '#4CAF50', tint: '#ECF6EC' },
  { key: '1-30' as const, label: '1–30 days', color: '#E8A22B', tint: '#FCF2E1' },
  { key: '31-60' as const, label: '31–60 days', color: '#E07B1E', tint: '#FBEDDF' },
  { key: '61-90' as const, label: '61–90 days', color: '#D9534F', tint: '#FAEAEA' },
  { key: '90+' as const, label: '90+ days', color: '#A5281F', tint: '#F5E6E4' },
];

// Exported (named) for index.test.tsx; the page's default export is unchanged.
export const ArAgingReportPanel: React.FC<{ filters: ReportFilters }> = ({ filters }) => {
  const navigate = useNavigate();
  const formatInstant = useFormatInstant();
  // null = "not clicked yet" → default to the first bucket that has invoices,
  // so the drill-down never opens on an empty table.
  const [clickedBucket, setClickedBucket] = useState<string | null>(null);

  const { data, isLoading } = useQuery<ArAgingReport>(
    ['reports', 'financial', 'ar-aging', filters.startDate, filters.endDate],
    async () => {
      const params = new URLSearchParams();
      if (filters.startDate) params.set('startDate', filters.startDate);
      if (filters.endDate) params.set('endDate', filters.endDate);
      return (await api.get(`/reports/financial/ar-aging?${params}`)).data;
    },
  );

  const handleExport = () => {
    if (!data) return;
    const rows: (string | number)[][] = [];
    for (const b of AGING_BUCKETS) {
      for (const inv of data.buckets[b.key].invoices) {
        // Invoice.dueDate is a CALENDAR day (DATE-KINDS §1.1: every computing
        // writer anchors it at noon UTC, barn ops at UTC midnight). The old
        // `new Date(x).toLocaleDateString()` re-zoned that anchor into the
        // browser's zone and exported the PREVIOUS day for every US clinic —
        // the live bug named in data-model-landmines.md. Parse the date PART.
        rows.push([b.label, inv.invoiceNumber, inv.horseName, inv.clientName, formatCalendarDate(inv.dueDate, 'M/d/yyyy'), inv.daysOverdue, inv.amount, inv.status]);
      }
    }
    exportCsv('ar-aging.csv', ['Bucket', 'Invoice #', 'Horse', 'Client', 'Due Date', 'Days Overdue', 'Amount', 'Status'], rows);
  };

  if (isLoading) return <Box py={3} display="flex" justifyContent="center"><CircularProgress size={24} /></Box>;
  if (!data) return null;

  const selectedBucket =
    clickedBucket ?? AGING_BUCKETS.find(b => data.buckets[b.key].count > 0)?.key ?? 'current';
  const activeBucketCfg = AGING_BUCKETS.find(b => b.key === selectedBucket)!;
  const activeBucket = data.buckets[selectedBucket as keyof typeof data.buckets];

  return (
    <Box data-testid="ar-aging-panel">
      <Box display="flex" justifyContent="flex-end" mb={1.75}>
        <Button size="small" startIcon={<ExportIcon />} onClick={handleExport} data-testid="ar-aging-export" sx={{ fontSize: '0.78rem', color: T.textSecondary }}>
          Export CSV
        </Button>
      </Box>

      {/* Proportion bar */}
      <Box sx={{ display: 'flex', height: 34, borderRadius: '8px', overflow: 'hidden', mb: 1.75 }}>
        {AGING_BUCKETS.map(b => {
          const bucket = data.buckets[b.key];
          const pct = data.totalAR > 0 ? (bucket.total / data.totalAR) * 100 : 0;
          const isSel = selectedBucket === b.key;
          return (
            <Box
              key={b.key}
              onClick={() => setClickedBucket(b.key)}
              sx={{
                width: `${pct}%`,
                bgcolor: b.color,
                opacity: isSel ? 1 : 0.5,
                boxShadow: isSel ? 'inset 0 0 0 2px rgba(0,0,0,.18)' : 'none',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'opacity .15s',
                minWidth: pct > 0 ? 4 : 0,
              }}
            >
              {pct >= 9 && (
                <Typography sx={{ color: '#fff', fontSize: '0.68rem', fontWeight: 700 }}>
                  {Math.round(pct)}%
                </Typography>
              )}
            </Box>
          );
        })}
      </Box>

      {/* Per-bucket stat cards */}
      <Box display="flex" gap={1.25} mb={2.25} sx={{ overflowX: { xs: 'auto', md: 'visible' } }}>
        {AGING_BUCKETS.map(b => {
          const bucket = data.buckets[b.key];
          const pct = data.totalAR > 0 ? Math.round((bucket.total / data.totalAR) * 100) : 0;
          const isSel = selectedBucket === b.key;
          return (
            <Box
              key={b.key}
              onClick={() => setClickedBucket(b.key)}
              data-testid={`ar-bucket-${b.key}`}
              sx={{
                flex: '1 1 0',
                minWidth: 100,
                border: isSel ? `2px solid ${b.color}` : `1px solid ${T.cardBorder}`,
                bgcolor: isSel ? b.tint : '#fff',
                borderRadius: '10px',
                p: '13px 14px',
                cursor: 'pointer',
                transition: 'all .15s',
              }}
            >
              <Box display="flex" alignItems="center" gap={0.75} mb={1}>
                <Box sx={{ width: 9, height: 9, borderRadius: '50%', bgcolor: b.color, flexShrink: 0 }} />
                <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: T.textSecondary }}>{b.label}</Typography>
              </Box>
              <Typography sx={{ fontSize: '1.25rem', fontWeight: 700, color: T.textPrimary, mt: '9px' }} data-testid={`ar-bucket-total-${b.key}`}>
                {formatDollars(bucket.total)}
              </Typography>
              <Typography sx={{ fontSize: '0.68rem', color: T.textMuted, mt: '2px' }} data-testid={`ar-bucket-count-${b.key}`}>
                {bucket.count} invoice{bucket.count !== 1 ? 's' : ''} · {pct}%
              </Typography>
            </Box>
          );
        })}
      </Box>

      {/* Drill-down table */}
      <TableContainer component={Paper} elevation={0} data-testid="ar-aging-table" sx={{ border: `1px solid ${T.cardBorder}`, borderRadius: '10px', overflow: 'hidden' }}>
        <Box
          display="flex"
          alignItems="center"
          gap={1.25}
          sx={{ bgcolor: activeBucketCfg.tint, px: '14px', py: '11px' }}
        >
          <Box sx={{ width: 10, height: 10, borderRadius: '2px', bgcolor: activeBucketCfg.color, flexShrink: 0 }} />
          <Typography sx={{ fontSize: '0.81rem', fontWeight: 600, color: T.textPrimary }} data-testid="ar-aging-table-heading">
            {activeBucketCfg.label} — {activeBucket?.count ?? 0} invoice{(activeBucket?.count ?? 0) !== 1 ? 's' : ''} · {formatDollars(activeBucket?.total ?? 0)}
          </Typography>
        </Box>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: '#FAFAF8' }}>
              {['Invoice #', 'Client', 'Horse', 'Invoiced', 'Overdue', 'Amount'].map(h => (
                <TableCell key={h} sx={{ fontWeight: 600, fontSize: '0.75rem', color: T.textSecondary, borderBottom: `1px solid ${T.cardBorder}` }}>
                  {h}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {(activeBucket?.invoices ?? []).length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 3, color: T.textSecondary, fontSize: '0.82rem' }}>
                  No invoices in this bucket.
                </TableCell>
              </TableRow>
            ) : (
              (activeBucket?.invoices ?? []).map(inv => (
                <TableRow
                  key={inv.id}
                  onClick={() => navigate(`/billing?invoice=${encodeURIComponent(inv.invoiceNumber)}`)}
                  data-testid="ar-aging-row"
                  sx={{
                    cursor: 'pointer',
                    '&:hover': { bgcolor: '#FBF8F4' },
                    '&:not(:last-child) td': { borderBottom: `1px solid #F0EBE3` },
                  }}
                >
                  <TableCell sx={{ fontSize: '0.8rem', fontWeight: 600, color: T.brand }} data-testid="ar-aging-invoice-number">{inv.invoiceNumber}</TableCell>
                  <TableCell sx={{ fontSize: '0.8rem' }}>{inv.clientName}</TableCell>
                  <TableCell sx={{ fontSize: '0.8rem', color: T.textSecondary }}>{inv.horseName}</TableCell>
                  {/* INSTANT (Invoice.issuedAt is `@default(now())`, DATE-KINDS §1.4) rendered
                      in the CLINIC's zone, not the viewer's browser zone. */}
                  <TableCell sx={{ fontSize: '0.8rem', color: T.textSecondary }} data-testid="ar-aging-issued">{formatInstant(inv.issuedAt, INSTANT_FORMATS.dateNumeric)}</TableCell>
                  <TableCell sx={{ fontSize: '0.8rem', fontWeight: inv.daysOverdue > 0 ? 600 : 400, color: inv.daysOverdue > 0 ? '#C0392B' : T.textSecondary }}>
                    {inv.daysOverdue > 0 ? `${inv.daysOverdue}d` : 'Not due'}
                  </TableCell>
                  <TableCell sx={{ fontSize: '0.8rem', fontWeight: 600, textAlign: 'right' }} data-testid="ar-aging-amount">{formatDollars(inv.amount)}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

// ─── Invoice Status Report ────────────────────────────────────────────────────

// The real Invoice.status values (payments.service recalculateInvoiceStatus):
// pending | overdue | partially_paid | paid | cancelled. The backend derives
// "overdue" for past-due pending invoices, so no other keys can occur.
const STATUS_CONFIG: Record<string, { color: string; label: string }> = {
  pending: { color: '#3F7CAC', label: 'Pending' },
  overdue: { color: '#D9534F', label: 'Overdue' },
  partially_paid: { color: '#E8A22B', label: 'Partially paid' },
  paid: { color: '#4CAF50', label: 'Paid' },
  cancelled: { color: '#9E948A', label: 'Cancelled / voided' },
};

const InvoiceStatusPanel: React.FC<{ filters: ReportFilters }> = ({ filters }) => {
  const { data, isLoading } = useQuery<InvoiceStatusRow[]>(
    ['reports', 'financial', 'invoice-status', filters.startDate, filters.endDate],
    async () => {
      const params = new URLSearchParams();
      if (filters.startDate) params.set('startDate', filters.startDate);
      if (filters.endDate) params.set('endDate', filters.endDate);
      return (await api.get(`/reports/financial/invoice-status?${params}`)).data;
    },
  );

  const handleExport = () => {
    if (!data) return;
    exportCsv('invoice-status.csv', ['Status', 'Count', 'Total ($)'], data.map(r => [r.status, r.count, r.total.toFixed(2)]));
  };

  if (isLoading) return <Box py={3} display="flex" justifyContent="center"><CircularProgress size={24} /></Box>;
  if (!data || data.length === 0) return (
    <Typography sx={{ py: 2, textAlign: 'center', color: T.textSecondary, fontSize: '0.82rem' }}>
      No invoice data for the selected period.
    </Typography>
  );

  const grandTotal = data.reduce((s, r) => s + r.total, 0);
  const grandCount = data.reduce((s, r) => s + r.count, 0);

  return (
    <Box>
      <Box display="flex" justifyContent="flex-end" mb={1.5}>
        <Button size="small" startIcon={<ExportIcon />} onClick={handleExport} sx={{ fontSize: '0.78rem', color: T.textSecondary }}>Export CSV</Button>
      </Box>
      <TableContainer component={Paper} elevation={0} sx={{ border: `1px solid ${T.cardBorder}`, borderRadius: '10px', overflow: 'hidden' }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: '#FAFAF8' }}>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: T.textSecondary }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: T.textSecondary }} align="right">Invoices</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: T.textSecondary }} align="right">Total</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map(row => {
              const cfg = STATUS_CONFIG[row.status] || { color: '#9E9E9E', label: row.status };
              return (
                <TableRow key={row.status} sx={{ '&:not(:last-child) td': { borderBottom: `1px solid #F0EBE3` } }}>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: cfg.color, flexShrink: 0 }} />
                      <Typography sx={{ fontSize: '0.82rem', color: T.textPrimary }}>{cfg.label}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="right" sx={{ fontSize: '0.82rem', color: T.textPrimary }}>{row.count.toLocaleString()}</TableCell>
                  <TableCell align="right" sx={{ fontSize: '0.82rem', fontWeight: 600, color: T.textPrimary }}>{formatDollars(row.total)}</TableCell>
                </TableRow>
              );
            })}
            <TableRow sx={{ bgcolor: '#FAFAF8' }}>
              <TableCell sx={{ fontWeight: 700, fontSize: '0.82rem', color: T.textPrimary }}>Total</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.82rem', color: T.textPrimary }}>{grandCount.toLocaleString()}</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.82rem', color: T.textPrimary }}>{formatDollars(grandTotal)}</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

// ─── Charges Not Yet Invoiced ─────────────────────────────────────────────────

// Exported (named) for index.test.tsx; the page's default export is unchanged.
export const ChargesUnbilledPanel: React.FC<{ filters: ReportFilters }> = ({ filters }) => {
  // `item.date` is Procedure.date, and it renders as an INSTANT in the CLINIC's
  // zone (DATE-KINDS §2.3 lists both sites in the Reports INSTANT group). The
  // column is MIXED at the COLUMN level (§1.2), so the choice is worth the note:
  //
  //   · The dominant writer is a real moment — ProcedureDialog's DateTimePicker
  //     straight through `procedures.service.ts:31` `new Date(dto.date)`. A
  //     date-part parse would push a 9 PM Eastern procedure into the next day.
  //   · The one CALENDAR writer is barn-ops bulk billing
  //     (`billing.service.ts:3198,3261` `date: new Date(dto.date)`), which
  //     reaches this `invoiceId: null` report when an invoice is deleted
  //     (`billing.service.ts:2189` nulls `invoiceId` on the linked procedures).
  //     ⚠ §1.2's evidence for that arm — "UTC midnight" — is STALE on this base:
  //     `Billing/BarnOperations.tsx:493` now posts `ymdToNoonUtcIso(...)` (W1/W2,
  //     merged in fd7987da). A NOON-UTC anchor is day-stable under a clinic-zone
  //     render for the whole UTC-12..UTC+11:59 band, i.e. every clinic we have.
  //
  // So the instant render is day-correct for BOTH arms going forward, and for
  // legacy midnight-anchored rows it is no worse than the browser-zone render it
  // replaces — while remote and relief staff finally see the clinic's day.
  const formatInstant = useFormatInstant();
  const { data, isLoading } = useQuery<ChargesUnbilledData>(
    ['reports', 'financial', 'charges-unbilled', filters.startDate, filters.endDate],
    async () => {
      const params = new URLSearchParams();
      if (filters.startDate) params.set('startDate', filters.startDate);
      if (filters.endDate) params.set('endDate', filters.endDate);
      return (await api.get(`/reports/financial/charges-unbilled?${params}`)).data;
    },
  );

  const handleExport = () => {
    if (!data) return;
    exportCsv('charges-unbilled.csv',
      ['Horse', 'Procedure', 'Category', 'Date', 'Performed By', 'Unit Price ($)'],
      data.items.map(i => [i.horseName, i.procedureName, i.category || '—', formatInstant(i.date, INSTANT_FORMATS.dateNumeric), i.performedBy || '—', i.unitPrice.toFixed(2)]),
    );
  };

  if (isLoading) return <Box py={3} display="flex" justifyContent="center"><CircularProgress size={24} /></Box>;
  if (!data) return null;

  return (
    <Box>
      {data.count > 0 && (
        <Box
          display="flex"
          alignItems="center"
          gap={1.25}
          sx={{ bgcolor: T.warningTint, borderLeft: `4px solid ${T.amber}`, borderRadius: '8px', px: '14px', py: '11px', mb: 1.75 }}
        >
          <WarningIcon sx={{ fontSize: 20, color: T.amber, flexShrink: 0 }} />
          <Typography sx={{ fontSize: '0.81rem', color: T.bandText }}>
            {data.count} charges totalling <strong>{formatDollars(data.total)}</strong> are not yet on an invoice — bill these to avoid revenue leakage.
          </Typography>
        </Box>
      )}
      <Box display="flex" justifyContent="flex-end" mb={1.5}>
        <Button size="small" startIcon={<ExportIcon />} onClick={handleExport} data-testid="charges-unbilled-export" sx={{ fontSize: '0.78rem', color: T.textSecondary }}>Export CSV</Button>
      </Box>
      {data.count === 0 ? (
        <Typography sx={{ py: 2, textAlign: 'center', color: T.textSecondary, fontSize: '0.82rem' }}>All charges have been invoiced.</Typography>
      ) : (
        <TableContainer component={Paper} elevation={0} sx={{ border: `1px solid ${T.cardBorder}`, borderRadius: '10px', overflow: 'hidden' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#FAFAF8' }}>
                {['Date', 'Provider', 'Horse / Client', 'Service', 'Amount'].map(h => (
                  <TableCell key={h} sx={{ fontWeight: 600, fontSize: '0.75rem', color: T.textSecondary }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {data.items.map(item => (
                <TableRow key={item.id} sx={{ '&:not(:last-child) td': { borderBottom: `1px solid #F0EBE3` } }}>
                  {/* Procedure.date — INSTANT in the clinic zone; see the panel-level note above. */}
                  <TableCell sx={{ fontSize: '0.8rem', color: T.textSecondary }} data-testid="charges-unbilled-date">{formatInstant(item.date, INSTANT_FORMATS.dateNumeric)}</TableCell>
                  <TableCell sx={{ fontSize: '0.8rem', color: T.textSecondary }}>{item.performedBy || '—'}</TableCell>
                  <TableCell sx={{ fontSize: '0.8rem' }}>{item.horseName}</TableCell>
                  <TableCell sx={{ fontSize: '0.8rem', fontWeight: 500 }}>{item.procedureName}</TableCell>
                  <TableCell sx={{ fontSize: '0.8rem', fontWeight: 600, textAlign: 'right' }}>{formatDollars(item.unitPrice)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

// ─── Payments / Deposit Summary ───────────────────────────────────────────────

const PAYMENT_METHOD_LABELS: Record<string, string> = {
  card: 'Card', cash: 'Cash', ach: 'ACH / Bank', check: 'Check', other: 'Other',
};

const PaymentsSummaryPanel: React.FC<{ filters: ReportFilters }> = ({ filters }) => {
  const { data, isLoading } = useQuery<PaymentsSummaryData>(
    ['reports', 'financial', 'payments-summary', filters.startDate, filters.endDate],
    async () => {
      const params = new URLSearchParams();
      if (filters.startDate) params.set('startDate', filters.startDate);
      if (filters.endDate) params.set('endDate', filters.endDate);
      return (await api.get(`/reports/financial/payments-summary?${params}`)).data;
    },
  );

  // `p.date` is `Payment.paymentDate || createdAt` — the canonical MIXED column
  // (DATE-KINDS §1.2 / §2.4 / W6), and it is resolved PER ROW here and in the
  // table below. Neither single helper works: a date-part parse would move a
  // 9 PM Eastern card payment into the next day's revenue, and the zoned render
  // that used to be here rolled every picked day back one day for every US
  // clinic. `formatPaymentDay` splits the two populations on the stored
  // spelling — the noon anchor / UTC midnight / a bare day are DAYS, anything
  // else is a moment shown in the clinic's zone. Same rule as the invoice
  // dialog and the backend (backend/src/common/payment-date.ts), so a payment
  // cannot read as one day on this report and another on the invoice.
  const clinicTz = useClinicTimezone();
  const handleExport = () => {
    if (!data) return;
    exportCsv('payments-summary.csv',
      ['Date', 'Invoice #', 'Horse', 'Method', 'Type', 'Reference', 'Amount ($)'],
      data.items.map(p => [formatPaymentDay(p.date, clinicTz, INSTANT_FORMATS.dateNumeric), p.invoiceNumber, p.horseName, p.paymentMethod, p.paymentType || '—', p.referenceNote || '—', (p.amountCents / 100).toFixed(2)]),
    );
  };

  if (isLoading) return <Box py={3} display="flex" justifyContent="center"><CircularProgress size={24} /></Box>;
  if (!data) return null;

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
        <Typography sx={{ fontSize: '0.82rem', color: T.textSecondary }}>
          {data.count} payment{data.count !== 1 ? 's' : ''} — <strong style={{ color: T.success }}>{formatCents(data.totalCents)}</strong> total collected
        </Typography>
        <Button size="small" startIcon={<ExportIcon />} onClick={handleExport} sx={{ fontSize: '0.78rem', color: T.textSecondary }}>Export CSV</Button>
      </Box>
      {data.count === 0 ? (
        <Typography sx={{ py: 2, textAlign: 'center', color: T.textSecondary, fontSize: '0.82rem' }}>No payments recorded for the selected period.</Typography>
      ) : (
        <TableContainer component={Paper} elevation={0} sx={{ border: `1px solid ${T.cardBorder}`, borderRadius: '10px', overflow: 'hidden' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#FAFAF8' }}>
                {['Date', 'Patient', 'Method', 'Reference', 'Amount'].map(h => (
                  <TableCell key={h} sx={{ fontWeight: 600, fontSize: '0.75rem', color: T.textSecondary }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {data.items.map(p => (
                <TableRow key={p.id} sx={{ '&:not(:last-child) td': { borderBottom: `1px solid #F0EBE3` } }}>
                  <TableCell sx={{ fontSize: '0.8rem', color: T.textSecondary }} data-testid="payments-summary-date">{formatPaymentDay(p.date, clinicTz, INSTANT_FORMATS.dateNumeric)}</TableCell>
                  <TableCell sx={{ fontSize: '0.8rem' }}>{p.horseName}</TableCell>
                  <TableCell sx={{ fontSize: '0.8rem' }}>
                    <Chip label={PAYMENT_METHOD_LABELS[p.paymentMethod] || p.paymentMethod} size="small" sx={{ fontSize: '0.68rem', height: 20 }} />
                  </TableCell>
                  <TableCell sx={{ fontSize: '0.8rem', color: T.textSecondary }}>{p.referenceNote || '—'}</TableCell>
                  <TableCell sx={{ fontSize: '0.8rem', fontWeight: 600, textAlign: 'right' }}>{formatCents(p.amountCents)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

// ─── Shared AR types + email status config ────────────────────────────────────

interface ArCommEntry {
  invoiceId: string;
  invoiceNumber: string;
  horseName: string;
  clientName: string;
  clientEmail: string | null;
  issuedAt: string;
  dueDate: string | null;
  amount: number;
  daysOverdue: number;
  lastContact: string | null;
  lastEmailStatus: string | null;
  lastEmailSentAt: string | null;
  emailBounced: boolean;
  contactCount: number;
}
interface ArCommunicationsData { entries: ArCommEntry[]; totalOverdue: number; }

const EMAIL_STATUS_CHIP: Record<string, { label: string; color: string; bg: string }> = {
  sent:      { label: 'Sent',      color: T.textSecondary, bg: '#F0EBE3' },
  delivered: { label: 'Delivered', color: '#2E7D32',       bg: '#E7F4E8' },
  opened:    { label: 'Opened',    color: '#1565C0',       bg: '#E3F0FB' },
  bounced:   { label: 'Bounced',   color: '#C0392B',       bg: '#FBEDEA' },
  failed:    { label: 'Failed',    color: '#C0392B',       bg: '#FBEDEA' },
};

// Shared by TopOverduePanel and ArCommunicationsPanel. A failed send MUST be
// loud — with no onError, the spinner just stops and staff walk away believing
// the client was dunned when no email went out.
const useSendArReminder = () => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  return useMutation(
    (invoiceId: string) => api.post(`/outbound-emails/ar-reminder/${invoiceId}`).then(r => r.data),
    {
      onSuccess: () => {
        // Keep the dashboard panel AND the financial-tab log in sync.
        queryClient.invalidateQueries(['reports', 'financial', 'ar-communications-dashboard']);
        queryClient.invalidateQueries(['reports', 'financial', 'ar-communications']);
      },
      onError: (err: any) => {
        enqueueSnackbar(
          err?.response?.data?.message ?? 'Failed to send the reminder email — the client was NOT contacted.',
          { variant: 'error' },
        );
      },
    },
  );
};

// ─── Top Overdue Accounts Panel (Dashboard tab) ───────────────────────────────

const TopOverduePanel: React.FC = () => {
  const { data, isLoading } = useQuery<ArCommunicationsData>(
    ['reports', 'financial', 'ar-communications-dashboard'],
    async () => (await api.get('/reports/financial/ar-communications')).data,
  );

  const sendReminder = useSendArReminder();

  // Largest balances first — "top overdue" = the most money at risk, not the
  // oldest due date.
  const top5 = [...(data?.entries ?? [])].sort((a, b) => b.amount - a.amount).slice(0, 5);

  return (
    <Paper elevation={0} sx={{ p: 2, border: `1px solid ${T.cardBorder}`, borderRadius: '12px', height: '100%' }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
        <Box>
          <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: T.textPrimary }}>Top overdue accounts</Typography>
          {data && (
            <Typography sx={{ fontSize: '0.72rem', color: T.textSecondary, mt: '2px' }}>
              {data.entries.length} overdue · <strong style={{ color: T.error }}>{formatDollars(data.totalOverdue)}</strong> total
            </Typography>
          )}
        </Box>
        {data && data.entries.length > 5 && (
          <Typography sx={{ fontSize: '0.72rem', color: T.textMuted }}>showing top 5</Typography>
        )}
      </Box>

      {isLoading && (
        <Box py={3} display="flex" justifyContent="center"><CircularProgress size={20} /></Box>
      )}

      {!isLoading && top5.length === 0 && (
        <Box py={3} textAlign="center">
          <Typography sx={{ fontSize: '0.82rem', color: T.textSecondary }}>No overdue invoices — great work!</Typography>
        </Box>
      )}

      {top5.map(e => {
        const isSending = sendReminder.isLoading && (sendReminder.variables as string) === e.invoiceId;
        const statusCfg = e.lastEmailStatus ? EMAIL_STATUS_CHIP[e.lastEmailStatus] : null;
        return (
          <Box
            key={e.invoiceId}
            display="flex"
            alignItems="center"
            gap={1}
            py={1}
            sx={{ borderBottom: `1px solid #F0EBE3`, '&:last-child': { borderBottom: 'none' } }}
          >
            <Box flex={1} minWidth={0}>
              <Box display="flex" alignItems="center" gap={0.75}>
                <Typography sx={{ fontSize: '0.82rem', fontWeight: 500, color: T.textPrimary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {e.horseName}
                </Typography>
                <Chip
                  label={`${e.daysOverdue}d`}
                  size="small"
                  sx={{
                    fontSize: '0.68rem', height: 18, flexShrink: 0,
                    bgcolor: e.daysOverdue > 90 ? T.errorTint : e.daysOverdue > 30 ? T.warningTint : '#FBF8F4',
                    color: e.daysOverdue > 90 ? T.error : e.daysOverdue > 30 ? T.warningText : T.textSecondary,
                    fontWeight: 600,
                  }}
                />
              </Box>
              <Box display="flex" alignItems="center" gap={0.75}>
                <Typography sx={{ fontSize: '0.72rem', color: T.textSecondary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {e.clientName}
                </Typography>
                {statusCfg && (
                  <Chip
                    label={statusCfg.label}
                    size="small"
                    sx={{ fontSize: '0.62rem', height: 16, flexShrink: 0, bgcolor: statusCfg.bg, color: statusCfg.color, fontWeight: 600 }}
                  />
                )}
              </Box>
            </Box>
            <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: T.textPrimary, flexShrink: 0 }}>
              {formatDollars(e.amount)}
            </Typography>
            <Tooltip title={!e.clientEmail ? 'No email on file' : e.emailBounced ? 'Email bounced' : 'Send reminder'}>
              <span>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={isSending ? <CircularProgress size={10} /> : <SendIcon />}
                  disabled={isSending || !e.clientEmail || e.emailBounced}
                  onClick={() => sendReminder.mutate(e.invoiceId)}
                  sx={{
                    fontSize: '0.68rem', height: 24, minWidth: 0, px: 1, flexShrink: 0,
                    borderColor: T.cardBorder, color: T.brand,
                    '&:hover': { borderColor: T.brand, bgcolor: T.bandBg },
                  }}
                >
                  {isSending ? '…' : 'Remind'}
                </Button>
              </span>
            </Tooltip>
          </Box>
        );
      })}
    </Paper>
  );
};

// ─── AR by Provider Panel ─────────────────────────────────────────────────────

interface ArByProviderRow { provider: string; invoiceCount: number; totalAR: number; overdueAR: number; }
interface ArByProviderData { rows: ArByProviderRow[]; totalAR: number; }

const ArByProviderPanel: React.FC<{ filters: ReportFilters }> = ({ filters }) => {
  const { data, isLoading } = useQuery<ArByProviderData>(
    ['reports', 'financial', 'ar-by-provider', filters.startDate, filters.endDate],
    async () => {
      const params = new URLSearchParams();
      if (filters.startDate) params.set('startDate', filters.startDate);
      if (filters.endDate) params.set('endDate', filters.endDate);
      return (await api.get(`/reports/financial/ar-by-provider?${params}`)).data;
    },
  );

  const handleExport = () => {
    if (!data) return;
    exportCsv('ar-by-provider.csv',
      ['Provider', 'Invoices', 'Total AR ($)', 'Overdue AR ($)'],
      data.rows.map(r => [r.provider, r.invoiceCount, r.totalAR.toFixed(2), r.overdueAR.toFixed(2)]),
    );
  };

  if (isLoading) return <Box py={3} display="flex" justifyContent="center"><CircularProgress size={24} /></Box>;
  if (!data) return null;

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
        <Typography sx={{ fontSize: '0.82rem', color: T.textSecondary }}>
          {data.rows.length} provider{data.rows.length !== 1 ? 's' : ''} — <strong style={{ color: T.brand }}>{formatDollars(data.totalAR)}</strong> outstanding
        </Typography>
        <Button size="small" startIcon={<ExportIcon />} onClick={handleExport} sx={{ fontSize: '0.78rem', color: T.textSecondary }}>Export CSV</Button>
      </Box>
      {data.rows.length === 0 ? (
        <Typography sx={{ py: 2, textAlign: 'center', color: T.textSecondary, fontSize: '0.82rem' }}>No outstanding AR for the selected period.</Typography>
      ) : (
        <TableContainer component={Paper} elevation={0} sx={{ border: `1px solid ${T.cardBorder}`, borderRadius: '10px', overflow: 'hidden' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#FAFAF8' }}>
                {['Provider', 'Invoices', 'Total AR', 'Overdue AR'].map(h => (
                  <TableCell key={h} sx={{ fontWeight: 600, fontSize: '0.75rem', color: T.textSecondary }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {data.rows.map(r => (
                <TableRow key={r.provider} sx={{ '&:not(:last-child) td': { borderBottom: `1px solid #F0EBE3` } }}>
                  <TableCell sx={{ fontSize: '0.8rem' }}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <PersonIcon sx={{ fontSize: 14, color: T.textMuted }} />
                      {r.provider}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ fontSize: '0.8rem', color: T.textSecondary }}>{r.invoiceCount}</TableCell>
                  <TableCell sx={{ fontSize: '0.8rem', fontWeight: 600 }}>{formatDollars(r.totalAR)}</TableCell>
                  <TableCell sx={{ fontSize: '0.8rem', color: r.overdueAR > 0 ? T.error : T.textSecondary, fontWeight: r.overdueAR > 0 ? 600 : 400 }}>
                    {r.overdueAR > 0 ? formatDollars(r.overdueAR) : '—'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

// ─── AR by Date Field Panel (shared by service date + billed date) ─────────────

interface ArByDateRow { period: string; invoiceCount: number; totalAR: number; }
interface ArByDateData { rows: ArByDateRow[]; totalAR: number; }

const ArByDatePanel: React.FC<{ filters: ReportFilters; endpoint: string; exportFilename: string; emptyLabel: string }> = ({ filters, endpoint, exportFilename, emptyLabel }) => {
  const { data, isLoading } = useQuery<ArByDateData>(
    ['reports', 'financial', endpoint, filters.startDate, filters.endDate],
    async () => {
      const params = new URLSearchParams();
      if (filters.startDate) params.set('startDate', filters.startDate);
      if (filters.endDate) params.set('endDate', filters.endDate);
      return (await api.get(`/reports/financial/${endpoint}?${params}`)).data;
    },
  );

  const handleExport = () => {
    if (!data) return;
    exportCsv(exportFilename,
      ['Period', 'Invoices', 'Total AR ($)'],
      data.rows.map(r => [r.period, r.invoiceCount, r.totalAR.toFixed(2)]),
    );
  };

  if (isLoading) return <Box py={3} display="flex" justifyContent="center"><CircularProgress size={24} /></Box>;
  if (!data) return null;

  return (
    // data-testid anchors: e2e/specs/ar-by-date-reports.spec.ts asserts the
    // server-rendered `period` labels here across the timezone matrix.
    <Box data-testid={`ar-by-date-panel-${endpoint}`}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
        <Typography sx={{ fontSize: '0.82rem', color: T.textSecondary }} data-testid="ar-by-date-summary">
          {data.rows.length} period{data.rows.length !== 1 ? 's' : ''} — <strong style={{ color: T.brand }}>{formatDollars(data.totalAR)}</strong> outstanding
        </Typography>
        <Button size="small" startIcon={<ExportIcon />} onClick={handleExport} sx={{ fontSize: '0.78rem', color: T.textSecondary }}>Export CSV</Button>
      </Box>
      {data.rows.length === 0 ? (
        <Typography sx={{ py: 2, textAlign: 'center', color: T.textSecondary, fontSize: '0.82rem' }}>{emptyLabel}</Typography>
      ) : (
        <TableContainer component={Paper} elevation={0} sx={{ border: `1px solid ${T.cardBorder}`, borderRadius: '10px', overflow: 'hidden' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#FAFAF8' }}>
                {['Period', 'Invoices', 'Total AR'].map(h => (
                  <TableCell key={h} sx={{ fontWeight: 600, fontSize: '0.75rem', color: T.textSecondary }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {data.rows.map(r => (
                <TableRow key={r.period} sx={{ '&:not(:last-child) td': { borderBottom: `1px solid #F0EBE3` } }} data-testid="ar-by-date-row">
                  <TableCell sx={{ fontSize: '0.8rem' }}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <CalendarMonthIcon sx={{ fontSize: 14, color: T.textMuted }} />
                      <span data-testid="ar-by-date-period">{r.period}</span>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ fontSize: '0.8rem', color: T.textSecondary }} data-testid="ar-by-date-count">{r.invoiceCount}</TableCell>
                  <TableCell sx={{ fontSize: '0.8rem', fontWeight: 600 }} data-testid="ar-by-date-total">{formatDollars(r.totalAR)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

// ─── AR Communications Panel ─────────────────────────────────────────────────

// Exported (named) for index.test.tsx; the page's default export is unchanged.
export const ArCommunicationsPanel: React.FC<{ filters: ReportFilters }> = ({ filters }) => {
  const navigate = useNavigate();
  // Both dates on this worklist are INSTANTS: `lastContact` is
  // ClientCommunication.dateTime (a DateTimePicker moment, DATE-KINDS §1.3) and
  // `lastEmailSentAt` is OutboundEmail.sentAt (`@default(now())`, §1.4). Render
  // them in the CLINIC's zone rather than whichever browser is viewing.
  const formatInstant = useFormatInstant();
  const { data, isLoading } = useQuery<ArCommunicationsData>(
    ['reports', 'financial', 'ar-communications', filters.startDate, filters.endDate],
    async () => {
      const params = new URLSearchParams();
      if (filters.startDate) params.set('startDate', filters.startDate);
      if (filters.endDate) params.set('endDate', filters.endDate);
      return (await api.get(`/reports/financial/ar-communications?${params}`)).data;
    },
  );

  const sendReminder = useSendArReminder();

  const handleExport = () => {
    if (!data) return;
    exportCsv('ar-communications.csv',
      ['Invoice #', 'Horse', 'Client', 'Email', 'Amount ($)', 'Days Overdue', 'Last Contact', 'Email Status', 'Contacts'],
      data.entries.map(e => [
        e.invoiceNumber, e.horseName, e.clientName, e.clientEmail || '—',
        e.amount.toFixed(2), e.daysOverdue,
        formatInstant(e.lastContact, INSTANT_FORMATS.dateNumeric) || '—',
        e.lastEmailStatus || '—', e.contactCount,
      ]),
    );
  };

  if (isLoading) return <Box py={3} display="flex" justifyContent="center"><CircularProgress size={24} /></Box>;
  if (!data) return null;

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
        <Typography sx={{ fontSize: '0.82rem', color: T.textSecondary }}>
          {data.entries.length} overdue invoice{data.entries.length !== 1 ? 's' : ''} — <strong style={{ color: T.error }}>{formatDollars(data.totalOverdue)}</strong> total overdue
        </Typography>
        <Button size="small" startIcon={<ExportIcon />} onClick={handleExport} sx={{ fontSize: '0.78rem', color: T.textSecondary }}>Export CSV</Button>
      </Box>
      {data.entries.length === 0 ? (
        <Typography sx={{ py: 2, textAlign: 'center', color: T.textSecondary, fontSize: '0.82rem' }}>No overdue invoices — great work!</Typography>
      ) : (
        <TableContainer component={Paper} elevation={0} sx={{ border: `1px solid ${T.cardBorder}`, borderRadius: '10px', overflow: 'hidden' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#FAFAF8' }}>
                {['Invoice', 'Horse / Client', 'Amount', 'Days Overdue', 'Last Contact', 'Email Status', 'Actions'].map(h => (
                  <TableCell key={h} sx={{ fontWeight: 600, fontSize: '0.75rem', color: T.textSecondary }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {data.entries.map(e => {
                const statusCfg = e.lastEmailStatus ? EMAIL_STATUS_CHIP[e.lastEmailStatus] : null;
                const isSending = sendReminder.isLoading && (sendReminder.variables as string) === e.invoiceId;
                return (
                  <TableRow key={e.invoiceId} sx={{ '&:not(:last-child) td': { borderBottom: `1px solid #F0EBE3` } }}>
                    <TableCell
                      onClick={() => navigate(`/billing?invoice=${encodeURIComponent(e.invoiceNumber)}`)}
                      sx={{ fontSize: '0.8rem', fontFamily: 'monospace', color: T.brand, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                    >
                      {e.invoiceNumber}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.8rem' }}>
                      <Typography sx={{ fontSize: '0.8rem', fontWeight: 500 }}>{e.horseName}</Typography>
                      <Typography sx={{ fontSize: '0.72rem', color: T.textSecondary }}>{e.clientName}</Typography>
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.8rem', fontWeight: 600 }}>{formatDollars(e.amount)}</TableCell>
                    <TableCell>
                      <Chip
                        label={`${e.daysOverdue}d`}
                        size="small"
                        sx={{
                          fontSize: '0.72rem',
                          height: 20,
                          bgcolor: e.daysOverdue > 90 ? T.errorTint : e.daysOverdue > 30 ? T.warningTint : '#FBF8F4',
                          color: e.daysOverdue > 90 ? T.error : e.daysOverdue > 30 ? T.warningText : T.textSecondary,
                          fontWeight: 600,
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.78rem', color: T.textSecondary }} data-testid="ar-comm-last-contact">
                      {e.lastContact ? formatInstant(e.lastContact, INSTANT_FORMATS.dateNumeric) : <span style={{ color: T.textMuted }}>Never</span>}
                      {e.contactCount > 0 && <Typography sx={{ fontSize: '0.7rem', color: T.textMuted }}>{e.contactCount}× contacted</Typography>}
                    </TableCell>
                    <TableCell>
                      {statusCfg ? (
                        <Tooltip title={e.lastEmailSentAt ? `Sent ${formatInstant(e.lastEmailSentAt, INSTANT_FORMATS.dateNumeric)}` : ''}>
                          <Chip
                            label={statusCfg.label}
                            size="small"
                            icon={e.emailBounced ? <ErrorOutlineIcon style={{ fontSize: 12 }} /> : <EmailIcon style={{ fontSize: 12 }} />}
                            sx={{ fontSize: '0.72rem', height: 20, bgcolor: statusCfg.bg, color: statusCfg.color, fontWeight: 600 }}
                          />
                        </Tooltip>
                      ) : (
                        <Typography sx={{ fontSize: '0.78rem', color: T.textMuted }}>—</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Tooltip title={!e.clientEmail ? 'No email on file' : e.emailBounced ? 'Email bounced — update client email before retrying' : 'Send AR reminder email'}>
                        <span>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={isSending ? <CircularProgress size={12} /> : <SendIcon />}
                            disabled={isSending || !e.clientEmail || (e.emailBounced && true)}
                            onClick={() => sendReminder.mutate(e.invoiceId)}
                            sx={{
                              fontSize: '0.72rem',
                              height: 26,
                              borderColor: T.cardBorder,
                              color: T.brand,
                              '&:hover': { borderColor: T.brand, bgcolor: T.bandBg },
                            }}
                          >
                            {isSending ? 'Sending…' : 'Remind'}
                          </Button>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

// ─── Production by Provider Panel ─────────────────────────────────────────────

interface ProductionRow { provider: string; invoicesBilled: number; billed: number; collected: number }
interface ProductionData { rows: ProductionRow[]; totalBilled: number; totalCollected: number }

const ProductionByProviderPanel: React.FC<{ filters: ReportFilters }> = ({ filters }) => {
  const { data, isLoading } = useQuery<ProductionData>(
    ['reports', 'financial', 'production-by-provider', filters.startDate, filters.endDate],
    async () => {
      const params = new URLSearchParams();
      if (filters.startDate) params.set('startDate', filters.startDate);
      if (filters.endDate) params.set('endDate', filters.endDate);
      return (await api.get(`/reports/financial/production-by-provider?${params}`)).data;
    },
  );

  const handleExport = () => {
    if (!data) return;
    exportCsv('production-by-provider.csv',
      ['Provider', 'Invoices Billed', 'Billed ($)', 'Collected ($)'],
      data.rows.map(r => [r.provider, r.invoicesBilled, r.billed.toFixed(2), r.collected.toFixed(2)]),
    );
  };

  if (isLoading) return <Box py={3} display="flex" justifyContent="center"><CircularProgress size={24} /></Box>;
  if (!data) return null;

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
        <Typography sx={{ fontSize: '0.82rem', color: T.textSecondary }}>
          Billed <strong style={{ color: T.brand }}>{formatDollars(data.totalBilled)}</strong> · collected{' '}
          <strong style={{ color: T.success }}>{formatDollars(data.totalCollected)}</strong> in the period
        </Typography>
        <Button size="small" startIcon={<ExportIcon />} onClick={handleExport} sx={{ fontSize: '0.78rem', color: T.textSecondary }}>Export CSV</Button>
      </Box>
      {data.rows.length === 0 ? (
        <Typography sx={{ py: 2, textAlign: 'center', color: T.textSecondary, fontSize: '0.82rem' }}>No production in the selected period.</Typography>
      ) : (
        <TableContainer component={Paper} elevation={0} sx={{ border: `1px solid ${T.cardBorder}`, borderRadius: '10px', overflow: 'hidden' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#FAFAF8' }}>
                {['Provider', 'Invoices', 'Billed', 'Collected', 'Collection rate'].map(h => (
                  <TableCell key={h} sx={{ fontWeight: 600, fontSize: '0.75rem', color: T.textSecondary }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {data.rows.map(r => {
                const rate = r.billed > 0 ? Math.round((r.collected / r.billed) * 100) : null;
                return (
                  <TableRow key={r.provider} sx={{ '&:not(:last-child) td': { borderBottom: `1px solid #F0EBE3` } }}>
                    <TableCell sx={{ fontSize: '0.8rem' }}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <PersonIcon sx={{ fontSize: 14, color: T.textMuted }} />
                        {r.provider}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.8rem', color: T.textSecondary }}>{r.invoicesBilled}</TableCell>
                    <TableCell sx={{ fontSize: '0.8rem', fontWeight: 600 }}>{formatDollars(r.billed)}</TableCell>
                    <TableCell sx={{ fontSize: '0.8rem', fontWeight: 600, color: T.success }}>{formatDollars(r.collected)}</TableCell>
                    <TableCell sx={{ fontSize: '0.8rem', color: rate != null && rate < 70 ? T.warningText : T.textSecondary }}>
                      {rate != null ? `${rate}%` : '—'}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <Typography sx={{ fontSize: '0.72rem', color: T.textMuted, mt: 1 }}>
        Billed = invoices issued in the period (accrual). Collected = payments received in the period (cash) — a payment may belong to work billed earlier.
      </Typography>
    </Box>
  );
};

// ─── Client Balance Summary Panel ─────────────────────────────────────────────

interface ClientBalanceRow {
  clientId: string | null;
  clientName: string;
  hasEmail: boolean;
  invoiceCount: number;
  totalOwed: number;
  overdueOwed: number;
  maxDaysOverdue: number;
}
interface ClientBalancesData { rows: ClientBalanceRow[]; totalOwed: number }

const ClientBalancesPanel: React.FC = () => {
  const navigate = useNavigate();
  const { data, isLoading } = useQuery<ClientBalancesData>(
    ['reports', 'financial', 'client-balances'],
    async () => (await api.get('/reports/financial/client-balances')).data,
  );

  const handleExport = () => {
    if (!data) return;
    exportCsv('client-balances.csv',
      ['Client', 'Open Invoices', 'Total Owed ($)', 'Overdue ($)', 'Oldest Overdue (days)'],
      data.rows.map(r => [r.clientName, r.invoiceCount, r.totalOwed.toFixed(2), r.overdueOwed.toFixed(2), r.maxDaysOverdue]),
    );
  };

  if (isLoading) return <Box py={3} display="flex" justifyContent="center"><CircularProgress size={24} /></Box>;
  if (!data) return null;

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
        <Typography sx={{ fontSize: '0.82rem', color: T.textSecondary }}>
          {data.rows.length} client{data.rows.length !== 1 ? 's' : ''} owing — <strong style={{ color: T.brand }}>{formatDollars(data.totalOwed)}</strong> total (all time)
        </Typography>
        <Button size="small" startIcon={<ExportIcon />} onClick={handleExport} sx={{ fontSize: '0.78rem', color: T.textSecondary }}>Export CSV</Button>
      </Box>
      {data.rows.length === 0 ? (
        <Typography sx={{ py: 2, textAlign: 'center', color: T.textSecondary, fontSize: '0.82rem' }}>No outstanding client balances — great work!</Typography>
      ) : (
        <TableContainer component={Paper} elevation={0} sx={{ border: `1px solid ${T.cardBorder}`, borderRadius: '10px', overflow: 'hidden' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#FAFAF8' }}>
                {['Client', 'Open invoices', 'Total owed', 'Overdue', 'Oldest overdue', ''].map(h => (
                  <TableCell key={h} sx={{ fontWeight: 600, fontSize: '0.75rem', color: T.textSecondary }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {data.rows.map(r => (
                <TableRow key={r.clientId ?? r.clientName} sx={{ '&:not(:last-child) td': { borderBottom: `1px solid #F0EBE3` } }}>
                  <TableCell sx={{ fontSize: '0.8rem', fontWeight: 500 }}>{r.clientName}</TableCell>
                  <TableCell sx={{ fontSize: '0.8rem', color: T.textSecondary }}>{r.invoiceCount}</TableCell>
                  <TableCell sx={{ fontSize: '0.8rem', fontWeight: 600 }}>{formatDollars(r.totalOwed)}</TableCell>
                  <TableCell sx={{ fontSize: '0.8rem', color: r.overdueOwed > 0 ? T.error : T.textSecondary, fontWeight: r.overdueOwed > 0 ? 600 : 400 }}>
                    {r.overdueOwed > 0 ? formatDollars(r.overdueOwed) : '—'}
                  </TableCell>
                  <TableCell sx={{ fontSize: '0.8rem', color: T.textSecondary }}>
                    {r.maxDaysOverdue > 0 ? `${r.maxDaysOverdue}d` : '—'}
                  </TableCell>
                  <TableCell align="right">
                    {r.clientId && (
                      <Button
                        size="small"
                        variant="text"
                        onClick={() => navigate(`/billing?clientId=${r.clientId}`)}
                        sx={{ fontSize: '0.72rem', color: T.brand, textTransform: 'none' }}
                      >
                        View invoices →
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

// ─── Appointment Outcomes Panel (Appointments tab) ────────────────────────────

interface AppointmentOutcomes {
  since: string | null;
  total: number;
  kept: number;
  cancelled: number;
  noShow: number;
  cancellationRate: number;
  noShowRate: number;
  /** The cancelled and no-show visits behind the counts, newest first (at most 200). */
  appointments?: Array<{
    id: string;
    date: string;
    status: 'cancelled' | 'no_show';
    type: string;
    subject: string;
    owner: string | null;
    vet: string | null;
  }>;
}

const useAppointmentOutcomes = (filters: ReportFilters) => {
  const startDate = filters.startDate || ALL_TIME_START;
  return useQuery<AppointmentOutcomes>(
    ['reports', 'appointments', 'outcomes', startDate, filters.endDate],
    async () => {
      const params = new URLSearchParams({ startDate });
      if (filters.endDate) params.set('endDate', filters.endDate);
      return (await api.get(`/reports/appointments/outcomes?${params}`)).data;
    },
  );
};

const AppointmentOutcomesPanel: React.FC<{ filters: ReportFilters }> = ({ filters }) => {
  const { data, isLoading } = useAppointmentOutcomes(filters);

  const stat = (label: string, value: string, color: string) => (
    <Box sx={{ flex: 1, minWidth: 110, border: `1px solid ${T.cardBorder}`, borderRadius: '10px', p: '12px 14px' }}>
      <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.4px', color: T.textMuted }}>{label}</Typography>
      <Typography sx={{ fontSize: '1.3rem', fontWeight: 700, color, mt: '6px' }}>{value}</Typography>
    </Box>
  );

  return (
    <Paper elevation={0} sx={{ p: 2, border: `1px solid ${T.cardBorder}`, borderRadius: '12px' }}>
      <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: T.textPrimary, mb: 0.5 }}>Cancellations & no-shows</Typography>
      <Typography sx={{ fontSize: '0.75rem', color: T.textSecondary, mb: 1.5 }}>In the date range above · operational loss tracking</Typography>
      {isLoading || !data ? (
        <Box py={3} display="flex" justifyContent="center"><CircularProgress size={20} /></Box>
      ) : (
        <Box display="flex" gap={1.25} flexWrap="wrap">
          {stat('Booked', String(data.total), T.textPrimary)}
          {stat('Kept', String(data.kept), T.success)}
          {stat('Cancelled', `${data.cancelled} · ${data.cancellationRate}%`, data.cancellationRate > 15 ? T.error : T.warningText)}
          {stat('No-show', `${data.noShow} · ${data.noShowRate}%`, data.noShowRate > 5 ? T.error : T.textSecondary)}
        </Box>
      )}
    </Paper>
  );
};

/** Which appointments were cancelled or missed in the range; each row opens it on the Schedule. */
const CancelledAppointmentsList: React.FC<{ filters: ReportFilters }> = ({ filters }) => {
  const navigate = useNavigate();
  const formatInstant = useFormatInstant();
  const { data } = useAppointmentOutcomes(filters);
  const list = data?.appointments ?? [];
  if (!data || list.length === 0) return null;
  return (
    <Paper elevation={0} sx={{ p: 2, border: `1px solid ${T.cardBorder}`, borderRadius: '12px' }}>
      <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: T.textPrimary, mb: 0.5 }}>Cancelled and no-show appointments</Typography>
      <Typography sx={{ fontSize: '0.75rem', color: T.textSecondary, mb: 1 }}>Newest first. Click one to open it on the Schedule.</Typography>
      <TableContainer sx={{ maxHeight: 420 }}>
        <Table size="small" stickyHeader aria-label="Cancelled and no-show appointments">
          <TableHead>
            <TableRow>
              <TableCell>When</TableCell>
              <TableCell>Patient</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Provider</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {list.map((a) => {
              // Opens the Schedule on that day with this appointment's window open.
              const open = () => navigate('/appointments', { state: { openAppointment: { id: a.id, date: a.date } } });
              return (
                <TableRow
                  key={a.id}
                  hover
                  tabIndex={0}
                  role="link"
                  aria-label={`Open ${a.subject || 'the appointment'}, ${formatInstant(a.date, INSTANT_FORMATS.dateTime)}`}
                  onClick={open}
                  onKeyDown={(e) => e.key === 'Enter' && open()}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell sx={{ fontSize: '0.8rem', whiteSpace: 'nowrap' }}>{formatInstant(a.date, INSTANT_FORMATS.dateTime)}</TableCell>
                  <TableCell sx={{ fontSize: '0.8rem' }}>
                    <Box sx={{ fontWeight: 600 }}>{a.subject || '—'}</Box>
                    {a.owner && a.owner !== a.subject && <Box sx={{ fontSize: '0.75rem', color: T.textSecondary }}>{a.owner}</Box>}
                  </TableCell>
                  <TableCell sx={{ fontSize: '0.8rem' }}>{a.type}</TableCell>
                  <TableCell sx={{ fontSize: '0.8rem', color: a.vet ? T.textPrimary : T.textSecondary }}>{a.vet ?? 'Unassigned'}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={a.status === 'no_show' ? 'No-show' : 'Cancelled'}
                      sx={{ fontSize: '0.72rem', fontWeight: 600, bgcolor: a.status === 'no_show' ? '#FDECEC' : '#FCF0E3', color: a.status === 'no_show' ? T.error : T.warningText }}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      {list.length < data.cancelled + data.noShow && (
        <Typography sx={{ mt: 1, fontSize: '0.75rem', color: T.textSecondary }}>
          {`Showing the latest ${list.length} of ${data.cancelled + data.noShow}. Narrow the date range to see older ones.`}
        </Typography>
      )}
    </Paper>
  );
};

// ─── Empty State ──────────────────────────────────────────────────────────────

const EmptyState: React.FC<{ message: string; hint?: string }> = ({ message, hint }) => (
  <Box py={5} textAlign="center">
    <Typography variant="h6" color="text.secondary" fontWeight={500}>{message}</Typography>
    {hint && <Typography variant="body2" color="text.secondary" mt={0.5}>{hint}</Typography>}
  </Box>
);

// ─── Value Chip helper ────────────────────────────────────────────────────────

const ValueChip: React.FC<{ label: string; color?: string; bg?: string }> = ({ label, color = T.bandText, bg = T.bandBg }) => (
  <Box sx={{ display: 'inline-flex', alignItems: 'center', bgcolor: bg, color, fontSize: '0.81rem', fontWeight: 700, px: '11px', py: '4px', borderRadius: '7px' }}>
    {label}
  </Box>
);

// ─── Main Component ───────────────────────────────────────────────────────────

// Default to ALL TIME: AR is a point-in-time balance, so a current-month
// default silently hid every older receivable and contradicted the all-time
// KPI tiles shown right above the reports.
const defaultFilters = (): ReportFilters => ({ startDate: '', endDate: '' });

const Reports: React.FC = () => {
  // What this person may actually open (ADR-0035 A8). `App.tsx`'s ReportsRoute has already
  // decided they may open the PAGE; what varies is what is on it. The server decides per route
  // and `REPORT_REQUIREMENTS` mirrors that table.
  //
  // ⚠ THREE STATES, NOT TWO, and conflating the first two was the review's blocking defect. This
  // component calls `useRBAC()` — its OWN instance — and a fresh consumer starts with
  // `claims === null`, because the identity arrives only after that instance's
  // `getIdTokenResult` resolves. `permissions` was therefore `[]` on FIRST PAINT for everyone,
  // Owners included, every visit: every `can()` was false, no tab was shown, and the "none of the
  // reports are turned on for you" empty state rendered before the answer existed. Dev-bypass
  // seeds the identity synchronously, so no local walk, no Playwright run and no mocked-hook test
  // could see it. `loading` and `permissionsPending` are the two windows — the token and the
  // /users/me read — and until BOTH close the honest answer is "we do not know yet".
  const { claims, loading, permissionsPending, permissionsUnavailable } = useRBAC();
  const permissionsKnown = !loading && !permissionsPending;
  // An Owner always holds `view:reports` (it is owner-locked server-side), so an unanswered read
  // is not a reason to blank their page — ADR-0035 A8 / `auth/canOpenReports.ts`. For everyone
  // else an unanswered read is exactly that: not a refusal, so the page says so in place rather
  // than showing an empty state that reads as a SETTING.
  const isOwner = claims?.role === 'OWNER';
  const accessUnknown = reportsAccessUnknown(claims, permissionsUnavailable);
  const permissions = useMemo(() => claims?.permissions ?? [], [claims]);
  const can = useCallback(
    (key: string) => {
      // Before the answer arrives, nothing is shown and nothing is asked. `false` here is what
      // keeps every `enabled:` below false too, so the loading state fires no request it would
      // then have to cancel or explain.
      if (!permissionsKnown) return false;
      // The set could not be read. The server stays the authority either way: this decides what
      // to RENDER, and an Owner's own reports are not withheld over a failed read.
      if (accessUnknown) return isOwner;
      return canSeeReport(key, permissions);
    },
    [permissionsKnown, accessUnknown, isOwner, permissions],
  );
  // The `reports/financial` family carries ONE conjunction (`view:reports` + `view:financial_reports` +
  // `view:billing`), so the ten sections are one decision. It is NOT the whole money question:
  // `/reports/revenue` and `/reports/kpi-summary` take only the first two, so a person with both
  // Reports slugs and no `view:billing` sees the revenue trend and the KPI tiles and none of the
  // receivables sections. Driving the tiles from this flag hid UI whose queries were enabled on
  // the shorter conjunction and fired anyway — each surface asks for its OWN report id now.
  const canSeeFinancialSections = can('ar-aging');
  const visibleTabs = useMemo(
    () =>
      [
        {
          value: 'dashboard',
          label: 'Dashboard',
          shown:
            can('revenue') ||
            can('kpi-summary') ||
            can('ar-communications') ||
            can('appointment-types'),
        },
        { value: 'financial', label: 'Financial Reports', shown: canSeeFinancialSections },
        {
          value: 'appointments',
          label: 'Appointments',
          shown: can('appointment-types') || can('appointment-outcomes'),
        },
        {
          value: 'clinical',
          label: 'Clinical & Inventory',
          shown: can('inventory-status') || can('upcoming-vaccinations'),
        },
      ].filter(t => t.shown),
    [can, canSeeFinancialSections],
  );
  /** A KPI tile that jumps to a hidden tab would silently land on whichever tab IS first. */
  const tabShown = useCallback(
    (value: string) => visibleTabs.some(t => t.value === value),
    [visibleTabs],
  );
  const [currentTab, setCurrentTab] = useState('financial');
  // Land on Financial when it is there (the habit every owner has), else on whatever IS. DERIVED
  // rather than corrected in an effect: an effect would render the empty Financial tab for one
  // frame first, and `setCurrentTab` from a KPI tile would fight it.
  const activeTab = visibleTabs.some(t => t.value === currentTab)
    ? currentTab
    : visibleTabs[0]?.value ?? '';
  const [expandedReports, setExpandedReports] = useState<Record<string, boolean>>({
    'ar-aging': true,
    'invoice-status': false,
    'charges-unbilled': false,
    'payments-summary': false,
    'ar-by-provider': false,
    'ar-by-service-date': false,
    'ar-by-billed-date': false,
    'ar-communications': false,
    'client-balances': false,
    'production-by-provider': false,
  });
  const [pendingFilters, setPendingFilters] = useState<ReportFilters>(defaultFilters());
  const [appliedFilters, setAppliedFilters] = useState<ReportFilters>(defaultFilters());

  const toggleReport = useCallback((id: string) => {
    setExpandedReports(prev => ({ ...prev, [id]: !prev[id] }));
  }, []);

  const handleApplyFilters = () => setAppliedFilters({ ...pendingFilters });
  const handlePreset = (f: ReportFilters) => {
    setPendingFilters(f);
    setAppliedFilters(f);
  };

  // KPI summary
  const { data: kpi, isLoading: isLoadingKpiQuery, isError: kpiError } = useQuery<KpiSummary>(
    ['reports', 'kpi-summary'],
    async () => (await api.get('/reports/kpi-summary')).data,
    { refetchOnWindowFocus: false, enabled: can('kpi-summary') },
  );
  // On error, tiles show '—' rather than a fabricated "$0.00".
  // ⚠ `enabled: false` leaves `isLoading` TRUE in react-query v3, so a person without the
  // financial slugs would sit on skeleton tiles forever. The tiles are hidden entirely for them
  // (the KPI row asks `can('kpi-summary')` below), and this guard keeps the flag honest if that
  // ever changes.
  const isLoadingKpi = can('kpi-summary') && (isLoadingKpiQuery || kpiError);

  // Overview data. Each `enabled` mirrors the server's conjunction for that route, so a person
  // who would be refused never fires the request: no 403 in the console, no error panel, and
  // nothing for the offline fallback to paper over.
  const { data: revenueData = [] } = useQuery<RevenueData[]>(
    ['revenue'],
    withOfflineFallback(
      async () => (await api.get('/reports/revenue')).data,
      () => readRevenueByMonth() as Promise<RevenueData[]>,
    ),
    { enabled: can('revenue') },
  );
  // The Appointments tab follows the date range; the dashboard keeps the last 30 days, which is
  // also all the offline copy can count.
  const isOffline = useIsOffline();
  const typesRange = activeTab === 'appointments'
    ? { startDate: appliedFilters.startDate || ALL_TIME_START, endDate: appliedFilters.endDate }
    : null;
  const { data: appointmentTypes = [], isError: typesFailed, isLoading: typesLoading } = useQuery<AppointmentType[]>(
    ['appointment-types', typesRange?.startDate ?? '', typesRange?.endDate ?? ''],
    withOfflineFallback(
      async () => {
        const params = new URLSearchParams();
        if (typesRange?.startDate) params.set('startDate', typesRange.startDate);
        if (typesRange?.endDate) params.set('endDate', typesRange.endDate);
        return (await api.get(`/reports/appointments?${params}`)).data;
      },
      () =>
        (typesRange
          ? Promise.reject(new Error('A date range needs a connection.'))
          : readAppointmentTypes()) as Promise<AppointmentType[]>,
    ),
    { enabled: can('appointment-types') },
  );
  const typesTotal = appointmentTypes.reduce((n, t) => n + t.count, 0);
  const exportTypes = () =>
    exportCsv('appointments-by-type.csv', ['Type', 'Appointments', 'Share (%)'],
      appointmentTypes.map((t) => [t.type, t.count, typesTotal ? ((t.count / typesTotal) * 100).toFixed(1) : '0']));
  const clinicTz = useClinicTimezone();
  const { data: upcomingVaccinations = [] } = useQuery<VaccinationSchedule[]>(
    ['upcoming-vaccinations'],
    withOfflineFallback(
      async () => (await api.get('/reports/vaccinations')).data,
      () => readUpcomingVaccinations(clinicTz) as Promise<VaccinationSchedule[]>,
    ),
    { enabled: can('upcoming-vaccinations') },
  );
  const { data: inventoryStatus = [] } = useQuery<InventoryItem[]>(
    ['inventory-status'],
    withOfflineFallback(
      async () => (await api.get('/reports/inventory')).data,
      () => readInventoryStatus() as Promise<InventoryItem[]>,
    ),
    { enabled: can('inventory-status') },
  );

  const getInventoryStatusColor = (status: string) => {
    if (status === 'Low Stock') return '#F44336';
    if (status === 'Warning') return '#FFC107';
    return '#4CAF50';
  };

  // Needs attention items for Dashboard tab
  const needsAttention = [
    kpi && (kpi.overdueARCount > 0) && {
      icon: <EventBusyIcon sx={{ color: T.error, fontSize: 18 }} />,
      text: `Overdue AR is ${formatDollars(kpi.overdueAR)} across ${kpi.overdueARCount} invoice${kpi.overdueARCount !== 1 ? 's' : ''}`,
      level: 'error',
    },
    kpi && (kpi.lowStockItems > 0) && {
      icon: <StockIcon sx={{ color: T.error, fontSize: 18 }} />,
      text: `${kpi.lowStockItems} inventory item${kpi.lowStockItems !== 1 ? 's' : ''} below reorder point`,
      level: 'error',
    },
    kpi && (kpi.unbilledChargesCount > 0) && {
      icon: <RequestQuoteIcon sx={{ color: T.amber, fontSize: 18 }} />,
      text: `${kpi.unbilledChargesCount} charge${kpi.unbilledChargesCount !== 1 ? 's' : ''} (${formatDollars(kpi.unbilledCharges)}) not yet invoiced`,
      level: 'warning',
    },
    kpi && (kpi.openRecords > 0) && {
      icon: <FolderOpenIcon sx={{ color: T.amber, fontSize: 18 }} />,
      text: `${kpi.openRecords} medical record${kpi.openRecords !== 1 ? 's' : ''} awaiting sign-off`,
      level: 'warning',
    },
  ].filter(Boolean) as Array<{ icon: React.ReactNode; text: string; level: string }>;

  return (
    <Box sx={{ bgcolor: T.pageBg, minHeight: '100vh', p: { xs: '16px', sm: '24px 28px 32px' } }}>

      {/* Page header */}
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb="20px">
        <Box>
          <Typography sx={{ fontSize: { xs: '1.3rem', sm: '1.625rem' }, fontWeight: 700, letterSpacing: '-0.3px', color: T.textPrimary }}>
            Reports
          </Typography>
          <Typography sx={{ fontSize: '0.875rem', color: T.textSecondary, mt: '3px' }}>
            Practice performance and financial overview
          </Typography>
        </Box>
      </Box>

      {/* We do not know yet. The page's own spinner idiom, the one every panel below uses — no
          verdict, no request, and above all NOT the empty state, which would tell an Owner their
          reports are turned off every time they opened the page. */}
      {!permissionsKnown && (
        <Box data-testid="reports-permissions-loading" py={3} display="flex" justifyContent="center">
          <CircularProgress size={24} />
        </Box>
      )}

      {/* We could not find out, and this person is not an Owner. One sentence, in place — never a
          redirect (the route wrapper deliberately does not bounce on an unanswered read) and never
          the empty state, because "we could not check" is not "nothing is turned on". */}
      {permissionsKnown && accessUnknown && !isOwner && (
        <Box
          data-testid="reports-access-unknown"
          sx={{ bgcolor: T.bandBg, border: `1px solid ${T.cardBorder}`, borderRadius: '8px', px: '20px', py: '28px', textAlign: 'center' }}
        >
          <Typography sx={{ fontSize: '0.95rem', color: T.textSecondary }}>
            {REPORTS_ACCESS_UNKNOWN_MESSAGE}
          </Typography>
        </Box>
      )}

      {/* Nothing turned on: one calm sentence with the fix in it, never a blank page. Reachable
          only once the answer is IN and it really was an answer.
          ⚠ The click path is "Settings > Team members", not an arrow (Ali, 2026-09-21) — the
          spelling every sentence a clinic reads on the permission surfaces now uses, shared with
          `auth/screenAccess.noAccessMessage`, the permission-editor notes and the KB articles.
          `&gt;` rather than a bare `>` only because JSX text wants it escaped. */}
      {permissionsKnown && !accessUnknown && visibleTabs.length === 0 && (
        <Box
          data-testid="reports-empty-state"
          sx={{ bgcolor: T.bandBg, border: `1px solid ${T.cardBorder}`, borderRadius: '8px', px: '20px', py: '28px', textAlign: 'center' }}
        >
          <Typography sx={{ fontSize: '0.95rem', color: T.textSecondary }}>
            You can open Reports, but none of the reports are turned on for you. An owner or admin
            can change that in Settings &gt; Team members.
          </Typography>
        </Box>
      )}

      {/* The KPI row reads `/reports/kpi-summary`, whose conjunction is `view:reports` +
          `view:financial_reports` — NOT the three-slug `financial/*` one. Gating it on the
          receivables family hid the tiles from someone whose query fired anyway. */}
      {can('kpi-summary') && kpiError && (
        <Box sx={{ bgcolor: T.errorTint, border: `1px solid ${T.error}`, borderRadius: '8px', px: '14px', py: '10px', mb: '16px' }}>
          <Typography sx={{ fontSize: '0.82rem', color: T.error, fontWeight: 600 }}>
            Couldn't load practice KPIs — refresh to retry.
          </Typography>
        </Box>
      )}

      {/* KPI row — the same report id as the query it renders, so what is shown and what is
          asked for can never disagree. */}
      {can('kpi-summary') && (
      <Box
        display="flex"
        gap={{ xs: 1.25, md: 1.75 }}
        mb="24px"
        sx={{ flexDirection: { xs: 'column', md: 'row' } }}
      >
        <KpiHeroCard
          value={formatDollars(kpi?.mtdRevenue ?? 0)}
          trend={kpi?.mtdRevenueChange ?? null}
          loading={isLoadingKpi}
        />

        <Box
          sx={{
            flex: 1,
            display: 'grid',
            gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' },
            gap: { xs: '10px', md: '11px' },
          }}
        >
          {/* ⚠ A TILE IS A NAVIGATION CONTROL, so it may only exist when its destination does.
              `activeTab` falls back to `visibleTabs[0]` for an unknown value, so a tile pointing
              at a tab this person cannot see did not do nothing — it silently moved them to
              whatever tab happened to be first, on a click that promised receivables. Each tile
              below asks `tabShown(...)` for its OWN destination; "Open Records" has no onClick
              and so has no destination to check. */}
          {tabShown('financial') && (
          <KpiTile
            title="Outstanding AR"
            value={isLoadingKpi ? '—' : formatDollars(kpi?.outstandingAR ?? 0)}
            subtitle={`${kpi?.outstandingARCount ?? 0} open invoices`}
            icon={<ARIcon sx={{ fontSize: 17 }} />}
            onClick={() => { setCurrentTab('financial'); toggleReport('ar-aging'); }}
          />
          )}
          {tabShown('financial') && (
          <KpiTile
            title="Overdue AR"
            value={isLoadingKpi ? '—' : formatDollars(kpi?.overdueAR ?? 0)}
            subtitle={`${kpi?.overdueARCount ?? 0} invoices past due`}
            icon={<EventBusyIcon sx={{ fontSize: 17 }} />}
            alertLevel={(kpi?.overdueARCount ?? 0) > 0 ? 'error' : undefined}
            onClick={() => { setCurrentTab('financial'); toggleReport('ar-aging'); }}
          />
          )}
          {tabShown('financial') && (
          <KpiTile
            title="Unbilled Charges"
            value={isLoadingKpi ? '—' : formatDollars(kpi?.unbilledCharges ?? 0)}
            subtitle={`${kpi?.unbilledChargesCount ?? 0} charges not invoiced`}
            icon={<RequestQuoteIcon sx={{ fontSize: 17 }} />}
            alertLevel={(kpi?.unbilledChargesCount ?? 0) > 0 ? 'warning' : undefined}
            onClick={() => { setCurrentTab('financial'); toggleReport('charges-unbilled'); }}
          />
          )}
          {tabShown('appointments') && (
          <KpiTile
            title="Appointments Today"
            value={isLoadingKpi ? '—' : String(kpi?.appointmentsToday ?? 0)}
            subtitle="scheduled for today"
            icon={<EventIcon sx={{ fontSize: 17 }} />}
            onClick={() => setCurrentTab('appointments')}
          />
          )}
          <KpiTile
            title="Open Records"
            value={isLoadingKpi ? '—' : String(kpi?.openRecords ?? 0)}
            subtitle="awaiting sign-off"
            icon={<FolderOpenIcon sx={{ fontSize: 17 }} />}
            alertLevel={(kpi?.openRecords ?? 0) > 0 ? 'warning' : undefined}
          />
          {tabShown('clinical') && (
          <KpiTile
            title="Vaccines / Coggins Due"
            value={isLoadingKpi ? '—' : String(kpi?.vaccinesDueSoon ?? 0)}
            subtitle="within 30 days"
            icon={<VaccinesIcon sx={{ fontSize: 17 }} />}
            onClick={() => setCurrentTab('clinical')}
          />
          )}
          {tabShown('clinical') && (
          <KpiTile
            title="Low Stock Items"
            value={isLoadingKpi ? '—' : String(kpi?.lowStockItems ?? 0)}
            subtitle="below reorder point"
            icon={<StockIcon sx={{ fontSize: 17 }} />}
            alertLevel={(kpi?.lowStockItems ?? 0) > 0 ? 'error' : undefined}
            onClick={() => setCurrentTab('clinical')}
          />
          )}
        </Box>
      </Box>
      )}

      {/* Tabs. One tab left is not a choice, so the strip only appears when there is one. */}
      {visibleTabs.length > 1 && (
      <Box sx={{ display: 'flex', gap: '4px', borderBottom: `1px solid ${T.cardBorder}`, mb: '20px' }}>
        {visibleTabs.map(tab => (
          <Box
            key={tab.value}
            onClick={() => setCurrentTab(tab.value)}
            sx={{
              px: '18px',
              py: '13px',
              fontSize: '0.875rem',
              fontWeight: activeTab === tab.value ? 600 : 500,
              color: activeTab === tab.value ? T.brand : T.textSecondary,
              borderBottom: activeTab === tab.value ? `3px solid ${T.amber}` : '3px solid transparent',
              mb: '-1px',
              cursor: 'pointer',
              transition: 'color .2s',
              letterSpacing: '0.2px',
              whiteSpace: 'nowrap',
              '&:hover': { color: T.brand },
            }}
          >
            {tab.label}
          </Box>
        ))}
      </Box>
      )}

      {/* ── Dashboard Tab ── */}
      {activeTab === 'dashboard' && (
        <Grid container spacing={2}>
          {/* Reads `/reports/revenue` — `view:reports` + `view:financial_reports`, with NO
              `view:billing`, because it aggregates rather than lists invoices. Its own id. */}
          {can('revenue') && (
          <Grid item xs={12} md={7}>
            <Paper elevation={0} sx={{ p: '18px 20px', border: `1px solid ${T.cardBorder}`, borderRadius: '12px' }}>
              <Box mb={2}>
                <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: T.textPrimary }}>Revenue trend</Typography>
                <Typography sx={{ fontSize: '0.75rem', color: T.textSecondary }}>Last 6 months · revenue collected, by payment date</Typography>
              </Box>
              <Box height={170}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={revenueData} barCategoryGap="30%">
                    <CartesianGrid strokeDasharray="3 3" stroke="#EDE8E1" vertical={false} />
                    <XAxis dataKey="month" stroke={T.textMuted} tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
                    <YAxis stroke={T.textMuted} tick={{ fontSize: 11 }} axisLine={false} tickLine={false} tickFormatter={v => `$${(v / 1000).toFixed(0)}k`} />
                    <RechartsTooltip formatter={(v: number) => [formatDollars(v), 'Collected']} />
                    <Bar dataKey="amount" radius={[5, 5, 0, 0]}>
                      {revenueData.map((_, i) => (
                        <Cell key={i} fill={i === revenueData.length - 1 ? T.brand : '#D8C3A8'} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </Paper>
          </Grid>
          )}

          {/* "Needs attention" is derived entirely from the KPI summary, so it follows THAT id. */}
          {can('kpi-summary') && (
          <Grid item xs={12} md={5}>
            <Paper elevation={0} sx={{ p: '18px 20px', border: `1px solid ${T.cardBorder}`, borderRadius: '12px', height: '100%' }}>
              <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: T.textPrimary, mb: 2 }}>Needs attention</Typography>
              {needsAttention.length === 0 ? (
                <Box py={3} textAlign="center">
                  <Typography sx={{ fontSize: '0.82rem', color: T.textSecondary }}>All clear — no items need attention.</Typography>
                </Box>
              ) : (
                <Box display="flex" flexDirection="column" gap={1.5}>
                  {needsAttention.map((item, i) => (
                    <Box key={i} display="flex" alignItems="flex-start" gap={1.25}>
                      <Box sx={{ mt: '1px', flexShrink: 0 }}>{item.icon}</Box>
                      <Typography sx={{ fontSize: '0.82rem', color: T.textPrimary, lineHeight: 1.5 }}>{item.text}</Typography>
                    </Box>
                  ))}
                </Box>
              )}
            </Paper>
          </Grid>
          )}

          {can('appointment-types') && (
          <Grid item xs={12} md={6}>
            <Paper elevation={0} sx={{ p: 2, border: `1px solid ${T.cardBorder}`, borderRadius: '12px' }}>
              <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: T.textPrimary, mb: 0.5 }}>Appointment types</Typography>
              <Typography sx={{ fontSize: '0.75rem', color: T.textSecondary, mb: 1.5 }}>Last 30 days</Typography>
              <Box height={200} display="flex" justifyContent="center">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={appointmentTypes} dataKey="count" nameKey="type" cx="50%" cy="50%" outerRadius={80}>
                      {appointmentTypes.map((e, i) => <Cell key={i} fill={e.color} />)}
                    </Pie>
                    <RechartsTooltip />
                  </PieChart>
                </ResponsiveContainer>
              </Box>
              {appointmentTypes.length === 0 && <EmptyState message="No appointment data" hint="Appointments from the last 30 days will appear here." />}
            </Paper>
          </Grid>
          )}

          {/* Reads /reports/financial/ar-communications, so it takes THAT id's three-slug
              conjunction — `view:billing` included. */}
          {can('ar-communications') && (
          <Grid item xs={12} md={6}>
            <TopOverduePanel />
          </Grid>
          )}

        </Grid>
      )}

      {/* ── Financial Reports Tab ── */}
      {activeTab === 'financial' && (
        <Box>
          <FilterBar filters={pendingFilters} onChange={setPendingFilters} onApply={handleApplyFilters} onPreset={handlePreset} />

          <SectionHeader label="Accounts Receivable" />

          <ExpandableReport
            id="ar-aging"
            title="AR Aging Report"
            subtitle={`${formatDollars(kpi?.outstandingAR ?? 0)} outstanding (all time) · click a segment or card to drill in`}
            icon={<ARIcon fontSize="inherit" />}
            rightChip={kpi ? <ValueChip label={`${formatDollars(kpi.outstandingAR)} all time`} /> : undefined}
            isOpen={expandedReports['ar-aging']}
            onToggle={() => toggleReport('ar-aging')}
          >
            <ArAgingReportPanel filters={appliedFilters} />
          </ExpandableReport>

          <ExpandableReport
            id="invoice-status"
            title="Invoice Status Report"
            subtitle="Invoice census by status — gross invoice totals (before payments); AR reports below show net owed"
            icon={<ReceiptLongIcon fontSize="inherit" />}
            isOpen={expandedReports['invoice-status']}
            onToggle={() => toggleReport('invoice-status')}
          >
            <InvoiceStatusPanel filters={appliedFilters} />
          </ExpandableReport>

          <ExpandableReport
            id="ar-by-provider"
            title="AR by Provider"
            subtitle="Accounts receivable broken down by treating veterinarian"
            icon={<PersonIcon fontSize="inherit" />}
            isOpen={expandedReports['ar-by-provider']}
            onToggle={() => toggleReport('ar-by-provider')}
          >
            <ArByProviderPanel filters={appliedFilters} />
          </ExpandableReport>

          <ExpandableReport
            id="ar-by-service-date"
            title="AR by Service Date"
            subtitle="Outstanding invoices grouped by the month service was performed"
            icon={<CalendarMonthIcon fontSize="inherit" />}
            isOpen={expandedReports['ar-by-service-date']}
            onToggle={() => toggleReport('ar-by-service-date')}
          >
            <ArByDatePanel
              filters={appliedFilters}
              endpoint="ar-by-service-date"
              exportFilename="ar-by-service-date.csv"
              emptyLabel="No AR data for the selected service date range."
            />
          </ExpandableReport>

          <ExpandableReport
            id="ar-by-billed-date"
            title="AR by Billed Date"
            subtitle="Outstanding invoices grouped by the month the invoice was issued"
            icon={<ReceiptLongIcon fontSize="inherit" />}
            isOpen={expandedReports['ar-by-billed-date']}
            onToggle={() => toggleReport('ar-by-billed-date')}
          >
            <ArByDatePanel
              filters={appliedFilters}
              endpoint="ar-by-billed-date"
              exportFilename="ar-by-billed-date.csv"
              emptyLabel="No AR data for the selected billed date range."
            />
          </ExpandableReport>

          <ExpandableReport
            id="ar-communications"
            title="Collections Worklist"
            subtitle="Overdue invoices only — outreach history, email delivery status, and one-click reminders"
            icon={<ForumIcon fontSize="inherit" />}
            isOpen={expandedReports['ar-communications']}
            onToggle={() => toggleReport('ar-communications')}
          >
            <ArCommunicationsPanel filters={appliedFilters} />
          </ExpandableReport>

          <ExpandableReport
            id="client-balances"
            title="Client Balance Summary"
            subtitle="Who owes what, per client — co-ownership and third-party payers resolved; balances are all-time"
            icon={<PersonIcon fontSize="inherit" />}
            isOpen={expandedReports['client-balances']}
            onToggle={() => toggleReport('client-balances')}
          >
            <ClientBalancesPanel />
          </ExpandableReport>

          <SectionHeader label="Billing & Invoicing" />

          <ExpandableReport
            id="charges-unbilled"
            title="Charges Not Yet Invoiced"
            subtitle="Procedures recorded but not yet added to an invoice — prevents revenue leakage"
            icon={<RequestQuoteIcon fontSize="inherit" />}
            rightChip={
              kpi && kpi.unbilledChargesCount > 0
                ? <ValueChip label={`${formatDollars(kpi.unbilledCharges)} · ${kpi.unbilledChargesCount}`} color={T.warningText} bg={T.warningTint} />
                : undefined
            }
            isOpen={expandedReports['charges-unbilled']}
            onToggle={() => toggleReport('charges-unbilled')}
          >
            <ChargesUnbilledPanel filters={appliedFilters} />
          </ExpandableReport>

          <ExpandableReport
            id="payments-summary"
            title="Payments / Deposit Summary"
            subtitle="All succeeded payments in the period — use for daily cash reconciliation"
            icon={<PaymentsIcon fontSize="inherit" />}
            isOpen={expandedReports['payments-summary']}
            onToggle={() => toggleReport('payments-summary')}
          >
            <PaymentsSummaryPanel filters={appliedFilters} />
          </ExpandableReport>

          <SectionHeader label="Revenue" />

          <ExpandableReport
            id="production-by-provider"
            title="Production by Provider"
            subtitle="Billed vs collected per veterinarian — invoices attributed via appointment assignments"
            icon={<TrendingUpIcon fontSize="inherit" />}
            isOpen={expandedReports['production-by-provider']}
            onToggle={() => toggleReport('production-by-provider')}
          >
            <ProductionByProviderPanel filters={appliedFilters} />
          </ExpandableReport>
        </Box>
      )}

      {/* ── Appointments Tab ── */}
      {activeTab === 'appointments' && (
        <Box>
        <FilterBar filters={pendingFilters} onChange={setPendingFilters} onApply={handleApplyFilters} onPreset={handlePreset} />
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Paper elevation={0} sx={{ p: 2, border: `1px solid ${T.cardBorder}`, borderRadius: '12px' }}>
              <Box display="flex" alignItems="flex-start" justifyContent="space-between" gap={1}>
                <Box>
                  <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: T.textPrimary, mb: 0.5 }}>Appointments by type</Typography>
                  <Typography sx={{ fontSize: '0.75rem', color: T.textSecondary, mb: 1.5 }}>In the date range above, including the types your clinic added</Typography>
                </Box>
                {appointmentTypes.length > 0 && (
                  <Button size="small" startIcon={<ExportIcon />} onClick={exportTypes} sx={{ flex: 'none', whiteSpace: 'nowrap', fontSize: '0.78rem', color: T.textSecondary }}>Export CSV</Button>
                )}
              </Box>
              <Box height={280} display="flex" justifyContent="center">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={appointmentTypes} dataKey="count" nameKey="type" cx="50%" cy="50%" outerRadius={100} label>
                      {appointmentTypes.map((e, i) => <Cell key={i} fill={e.color} />)}
                    </Pie>
                    <RechartsTooltip />
                  </PieChart>
                </ResponsiveContainer>
              </Box>
              {appointmentTypes.length > 0 && (
                <TableContainer>
                  <Table size="small" aria-label="Appointments by type">
                    <TableHead>
                      <TableRow>
                        <TableCell>Type</TableCell>
                        <TableCell align="right">Appointments</TableCell>
                        <TableCell align="right">Share</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {appointmentTypes.map((t) => (
                        <TableRow key={t.type}>
                          <TableCell>
                            <Box component="span" sx={{ display: 'inline-block', width: 9, height: 9, borderRadius: '50%', bgcolor: t.color, mr: 1 }} />
                            {t.type}
                          </TableCell>
                          <TableCell align="right">{t.count}</TableCell>
                          <TableCell align="right">{typesTotal ? `${((t.count / typesTotal) * 100).toFixed(1)}%` : '0%'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
              {appointmentTypes.length === 0 && (
                typesLoading
                  ? <Box py={3} display="flex" justifyContent="center"><CircularProgress size={20} /></Box>
                  : typesFailed
                    ? <EmptyState message="Could not load this report" hint={isOffline ? 'A date range needs a connection.' : 'Try again in a moment.'} />
                    : <EmptyState message="No appointment data" hint="Appointments in the date range will appear here." />
              )}
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <AppointmentOutcomesPanel filters={appliedFilters} />
          </Grid>
          <Grid item xs={12}>
            <CancelledAppointmentsList filters={appliedFilters} />
          </Grid>
        </Grid>
        </Box>
      )}

      {/* ── Clinical & Inventory Tab ── */}
      {activeTab === 'clinical' && (
        <Grid container spacing={2}>
          {/* The two halves of this tab ride DIFFERENT slugs — view:inventory and
              view:medical_records — so it is the one tab that can be half-visible. */}
          {can('inventory-status') && (
          <Grid item xs={12} md={6}>
            <Paper elevation={0} sx={{ p: 2, border: `1px solid ${T.cardBorder}`, borderRadius: '12px' }}>
              <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                <StockIcon sx={{ fontSize: 18, color: T.error }} />
                <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: T.textPrimary }}>Low stock items</Typography>
              </Box>
              <Typography sx={{ fontSize: '0.75rem', color: T.textSecondary, mb: 2 }}>Items requiring attention</Typography>
              {inventoryStatus.length === 0 ? (
                <EmptyState message="All inventory levels are OK" hint="Items below minimum stock threshold will appear here." />
              ) : (
                inventoryStatus.map(item => (
                  <Box key={item.name} display="flex" justifyContent="space-between" alignItems="center" py={1} sx={{ borderBottom: `1px solid #F0EBE3` }}>
                    <Typography sx={{ fontSize: '0.82rem', color: T.textPrimary }}>{item.name}</Typography>
                    <Typography sx={{ fontSize: '0.78rem', color: getInventoryStatusColor(item.status), fontWeight: 600 }}>
                      {item.currentStock} left · reorder at {item.minRequired}
                    </Typography>
                  </Box>
                ))
              )}
            </Paper>
          </Grid>
          )}

          {can('upcoming-vaccinations') && (
          <Grid item xs={12} md={6}>
            <Paper elevation={0} sx={{ p: 2, border: `1px solid ${T.cardBorder}`, borderRadius: '12px' }}>
              <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                <VaccinesIcon sx={{ fontSize: 18, color: T.brand }} />
                <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: T.textPrimary }}>Vaccines / Coggins due soon</Typography>
              </Box>
              <Typography sx={{ fontSize: '0.75rem', color: T.textSecondary, mb: 2 }}>Due in the next 30 days</Typography>
              {upcomingVaccinations.length === 0 ? (
                <EmptyState message="No vaccinations due soon" hint="Horses due in the next 30 days will appear here." />
              ) : (
                /* Index-suffixed key: `horseName + vaccineType` alone is no longer
                 * unique now that every lab-result Coggins reports the FIXED
                 * `vaccineType` "Coggins / EIA" — two results for one horse inside
                 * the 30-day window collide. The list is read-only and re-sorted
                 * server-side, so positional identity is the honest key here. */
                upcomingVaccinations.map((v, i) => (
                  <Box key={`${v.horseName}|${v.vaccineType}|${i}`} display="flex" justifyContent="space-between" alignItems="center" py={1} sx={{ borderBottom: `1px solid #F0EBE3` }}>
                    <Box>
                      <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: T.textPrimary }}>{v.horseName}</Typography>
                      <Typography sx={{ fontSize: '0.75rem', color: T.textSecondary }}>{v.vaccineType}</Typography>
                    </Box>
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: T.warningText }}>{v.dueDate}</Typography>
                  </Box>
                ))
              )}
            </Paper>
          </Grid>
          )}

        </Grid>
      )}
    </Box>
  );
};

export default Reports;
